import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, QueryList, ViewChildren, ChangeDetectorRef, ApplicationRef, SimpleChanges, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import Handsontable from 'handsontable';
import { HotTableRegisterer } from '@handsontable/angular';
import { Subject, Subscription, finalize, Observable, firstValueFrom } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { SystemPrompt } from './system-prompt';
import { PromptLibrary, PromptCategory, PromptExample } from './prompt-library';
import { ToastrService } from 'ngx-toastr';
import { Config, DiffPatcher } from 'jsondiffpatch';
import { AIAgentService, AIAgentCallbacks } from './ai-agent.service';
import * as THREE from 'three';
import { MatDialog } from '@angular/material/dialog'; // Added MatDialog import
import { DrawingCanvasComponent } from '../drawing-canvas/drawing-canvas.component'; // Added DrawingCanvasComponent import
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AuthService } from '../services/auth.service';

// Define the type for a tab
// type Tab = {
//     id: number;
//     sheetId: number; // Add sheetId to associate tabs with sheets
//     prompt: string;
//     data: any[][];
//     attachments: any[];
//     chatHistory: { role: 'user' | 'assistant', content: string, expanded?: boolean }[];
//     isRunTab?: boolean; // Flag to identify tabs created by do5runs
// };

// Define type for chat history entries
type ChatMessage = {
    role: 'user' | 'assistant';
    content: string;
    expanded?: boolean;
};

// Type to represent the processing state for a specific sheet
interface SheetProcessingState {
    isSubmitting: boolean;
    isLoading: boolean;
    isProcessingResponse: boolean;
    isCheckingImage: boolean;
    isAnalyzingModel: boolean;
    loadingText: string;
}

@Component({
    selector: 'app-ai-prompt-editor',
    templateUrl: './ai-prompt-editor.component.html',
    styleUrls: ['./ai-prompt-editor.component.css']
})
export class AiPromptEditorComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() PROJECT: string = '';
    @Input() sheetid: number = 0;
    @Input() excels: any[] = [];
    @Input() renderer: any;
    @Input() scene: any;
    @Input() camera: any;
    @Input() project: any;

    @Output() copyTableData = new EventEmitter<any>();
    @Output() closeAIPromptEditor = new EventEmitter<void>();
    private apiUrl = 'https://us-central1-xbuild3d.cloudfunctions.net'; // Replace with your actual Firebase Function URL

    private hotRegisterer = new HotTableRegisterer();

    hotid = 'hotInstance';
    //    @ViewChild("hot", { static: false }) hotContainer!: ElementRef;
    @Input() showAIPromptEditor: boolean = false;
    @Input() sheetformulas: any;

    // Remove hotsettings if Handsontable is not used
    // hotsettings: any;
    aiPrompt: string = '';

    // Remove component-level chatHistory - Will use project.chatHistory[sheetid] directly
    // chatHistory: ChatMessage[] = [];
    attachments: any[] = [];
    currentSheetData: any[][] = []; // Data related to the current sheet (if needed)

    isLocalhost: boolean = false;

    @Output() aiPromptChangee = new EventEmitter<string>();

    @Output() aiIterationChange = new EventEmitter<any>();

    @Output() chatHistoryChange = new EventEmitter<any>();

    @Output() recreateModelRequest = new EventEmitter<{ sheetId: number, resolve: () => void }>();

    @ViewChildren('promptTextarea') promptTextareas!: QueryList<ElementRef>;
    @ViewChild('chatHistoryContainer') chatHistoryContainer!: ElementRef;

    currentTabData: any[][] = []; // Keep for now, maybe related to Handsontable, reassess later

    private hotInstance: Handsontable | null = null;

    aiProvider: 'claude' | 'gpt-4o' | 'o4-mini' | 'gpt-4.1' | 'o3' | 'gemini-2.5-flash' | 'gemini-2.5-flash-lite-preview-06-17' | 'gemini-2.5-pro' | 'deepseek' = 'gemini-2.5-pro';
    temperature: number = 0.7;
    agentMode: boolean = false; // Added agentMode property
    geminiThinkingEnabled: boolean = true; // Added thinking mode toggle for Gemini

    diffPatcher: DiffPatcher;
    showDiff: boolean = false;
    diffIndex: number = -1;

    // Abort controllers
    private abortController: AbortController | null = null;
    private checkAbortController: AbortController | null = null;
    private analyzeAbortController: AbortController | null = null;
    private destroy$ = new Subject<void>(); // For unsubscribing

    // Animation state (remains component-level)
    animatedSetButtonActive: boolean = false;
    animatedSetButtonMessageIndex: number = -1;
    animatedCheckButtonActive: boolean = false;

    tableMarkingType: string = 'values'; // Added back
    tableMarkingDropdown = false; // Added back
    screenshotDropdown = false; // Added back

    private manualCheckResult$ = new Subject<{ sheetId: number, result: string | null }>(); // Added Subject

    private readonly PADDING_FACTOR = 1.2; // 20% padding for zoom to fit

    userInput: string = '';
    aiResponse: string = '';
    isLoading: boolean = false;
    showConfig: boolean = false;
    selectedModel: string = 'gemini-1.5-flash-latest'; // Default model
    models: any[] = []; // To store model list
    showModelSelector: boolean = false;
    currentEditorInstance: any;
    currentProject: any;
    currentSystemPrompt: string = '';
    systemPrompts: any = SystemPrompt; // Corrected from SYSTEM_PROMPTS
    selectedSystemPromptKey: string = 'default'; // Default system prompt key
    showSystemPromptSelector: boolean = false;
    includeSpreadsheetData: boolean = false;
    activeSheetData: any[][] = [];
    streamedAiContent: string = '';
    isStreamingResponse: boolean = false;
    useStreamingForGemini: boolean = true; // Default to true, can be a checkbox
    generatedSubProjects: any[] = []; // To store generated sub-projects
    base64images: any[] = []; // Declare missing property
    base64pdfs: any[] = []; // Declare missing property
    selectedProvider: string = 'gemini'; // Declare missing property, default to gemini
    userCredits: number = 0;
    isImprovingPrompt: boolean = false;

    // Example prompts for 3D modeling
    examplePrompts = [
        {
            label: "Create Cube",
            prompt: "Create a simple cube with dimensions 10x10x10 units",
            description: "Generate a basic cube geometry"
        },
        {
            label: "House Structure",
            prompt: "Design a simple house with walls, roof, and a door. Make it 20 units wide, 15 units deep, and 12 units tall",
            description: "Create a basic house structure"
        },
        {
            label: "Beam Analysis",
            prompt: "Create an I-beam structure for structural analysis. Length should be 50 units with standard I-beam cross-section",
            description: "Generate structural beam for engineering analysis"
        },
        {
            label: "Bridge Design",
            prompt: "Design a simple truss bridge spanning 100 units with triangular support structures",
            description: "Create a basic bridge design"
        },
        {
            label: "Staircase",
            prompt: "Create a straight staircase with 12 steps, each step 10 units wide and 8 units deep",
            description: "Generate staircase geometry"
        },
        {
            label: "Circular Tower",
            prompt: "Design a cylindrical tower 8 units in diameter and 30 units tall with windows",
            description: "Create a tower structure"
        },
        {
            label: "Frame Structure",
            prompt: "Create a rectangular frame structure 20x15x10 units for building framework",
            description: "Generate building frame"
        },
        {
            label: "Optimize Design",
            prompt: "Analyze the current structure and suggest optimizations for material usage and structural integrity",
            description: "Get optimization suggestions"
        }
    ];

    // Prompt library menu properties
    showPromptLibraryMenu: boolean = false;
    promptLibraryCategories: PromptCategory[] = PromptLibrary.categories;
    selectedCategory: PromptCategory | null = null;
    selectedSubcategory: PromptCategory | null = null;

    // Bound event handlers for proper cleanup
    private boundHandleOutsideClick = this.handleOutsideClick.bind(this);
    private boundHandleWindowResize = this.handleWindowResize.bind(this);

    constructor(private http: HttpClient, private cdr: ChangeDetectorRef, private toastr: ToastrService, private appRef: ApplicationRef, private aiAgentService: AIAgentService, private zone: NgZone, private dialog: MatDialog, private afs: AngularFirestore, private authService: AuthService) {
        const config: Config = {
            arrays: {
                detectMove: true,
                includeValueOnMove: false
            },
            textDiff: {
                minLength: 60
            },
            propertyFilter: function (name, context) {
                return name.slice(0, 1) !== '$';
            },
            cloneDiffValues: false
        };
        this.diffPatcher = new DiffPatcher(config);
        this.isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    }

    ngOnInit() {
        this.currentSheetData = this.generateRandomData(5, 5);
        this.updateHandsontableData();
        this.ensureChatHistoryExists(this.sheetid);
        this.ensureSheetStateExists(this.sheetid); // Ensure initial state exists
        this.initializeMessageExpansionState(); // Initialize message states

        // Add click listener to close prompt library menu when clicking outside
        document.addEventListener('click', this.boundHandleOutsideClick);

        // Add window resize listener to reposition dropdown
        window.addEventListener('resize', this.boundHandleWindowResize);

        this.authService.authState.pipe(takeUntil(this.destroy$)).subscribe(user => {
            if (user) {
                this.afs.doc<any>(`users/${user.uid}`).valueChanges().pipe(takeUntil(this.destroy$)).subscribe(userData => {
                    if (userData && userData.credits !== undefined) {
                        this.userCredits = userData.credits;
                        this.cdr.detectChanges(); // Trigger change detection
                    } else {
                        this.userCredits = 0; // Default to 0 if no credits found or userData is null
                        this.cdr.detectChanges();
                    }
                });
            } else {
                this.userCredits = 0; // User not logged in, set credits to 0
                this.cdr.detectChanges();
            }
        });

        // Subscribe to manual check results
        this.manualCheckResult$
            .pipe(takeUntil(this.destroy$)) // Unsubscribe on destroy
            .subscribe(({ sheetId, result }) => {
                console.log(`[Manual Check - Subscription] Received result via Subject for sheet ${sheetId}:`, result ? result.substring(0, 100) + '...' : null);
                // Use optional chaining for safety
                if (this.project?.sheetStates?.[sheetId]?.isCheckingImage || true) { // Temporarily force update even if state is slightly off due to timing
                    // Logic to add result to history (needs to be inside zone.run for CDR)
                    this.zone.run(() => {
                        console.log(`[Manual Check - Subscription] Entering NgZone.run block for sheet ${sheetId}`);
                        if (result !== null) {
                            console.log(`[Manual Check - Subscription] Result is not null. Adding to history for sheet ${sheetId}`);
                            try {
                                this.ensureChatHistoryExists(sheetId);
                                const messageToAdd = { role: 'assistant', content: `**Manual Check Result:**\n${result}`, expanded: false };
                                this.project.chatHistory[sheetId].push(messageToAdd);
                                console.log(`[Manual Check - Subscription] Message pushed. Emitting history change.`);
                                this.chatHistoryChange.emit({ [sheetId]: this.project.chatHistory[sheetId] });
                                console.log(`[Manual Check - Subscription] Scrolling.`);
                                this.scrollChatToBottom();
                                console.log(`[Manual Check - Subscription] Triggering CDR.`);
                                this.cdr.detectChanges();
                                this.toastr.success('Image analysis completed', 'Analysis Complete');
                            } catch (historyError) {
                                console.error(`[Manual Check - Subscription] Error updating history for sheet ${sheetId}:`, historyError);
                                this.toastr.error('Failed to update chat history via subscription.', 'UI Error');
                            }
                        } else {
                            console.log(`[Manual Check - Subscription] Result was null for sheet ${sheetId}, not adding to history.`);
                        }
                        console.log(`[Manual Check - Subscription] Exiting NgZone.run block for sheet ${sheetId}`);
                    });
                } else {
                    // This case might happen if the finally block of testCheckImageContent runs before the Subject emits
                    console.warn(`[Manual Check - Subscription] Received result for sheet ${sheetId}, but component state indicates not loading/checking. Still attempting history update.`);
                    // Optionally, still try to update history here if needed, ensuring zone.run
                    this.zone.run(() => {
                        if (result !== null) { /* ... history update logic ... */ }
                    });
                }
            });
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.initializeHandsontable();
            this.scrollChatToBottom();
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        this.abortCurrentRequest();
        this.abortCheckRequest();
        this.abortAnalyzeRequest();

        // Remove click listener for prompt library menu
        document.removeEventListener('click', this.boundHandleOutsideClick);

        // Remove window resize listener
        window.removeEventListener('resize', this.boundHandleWindowResize);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['project'] || changes['sheetid']) {
            // Ensure state exists if project/sheetid changes
            this.ensureSheetStateExists(this.sheetid);
            this.ensureChatHistoryExists(this.sheetid);
            this.initializeMessageExpansionState(); // Re-initialize message states on change
            // Potentially scroll to bottom if sheet changes
            this.scrollChatToBottom();
            this.cdr.detectChanges(); // Detect changes after re-initialization
        }
    }

    initializeHandsontable() {
        // ... (Handsontable init logic remains the same)
    }

    updateHandsontableData() {
        // ... (Handsontable update logic remains the same)
    }

    // Helper to ensure state object exists for a sheet
    private ensureSheetStateExists(sheetId: number): void {
        if (!this.project) {
            console.warn(`AI Editor: ensureSheetStateExists called for sheet ${sheetId} before project input was received.`);
            return;
        }

        // Initialize sheetStates in project if it doesn't exist
        if (!this.project.sheetStates) {
            this.project.sheetStates = {};
        }

        if (!this.project.sheetStates[sheetId]) {
            this.project.sheetStates[sheetId] = {
                isSubmitting: false,
                isLoading: false,
                isProcessingResponse: false,
                isCheckingImage: false,
                isAnalyzingModel: false,
                loadingText: ''
            };
        }
    }

    generateRandomData(rows: number, cols: number): any[][] {
        const data = [];
        for (let i = 0; i < rows; i++) {
            const row = [];
            for (let j = 0; j < cols; j++) {
                row.push(Math.floor(Math.random() * 100));
            }
            data.push(row);
        }
        return data;
    }

    async onAISubmit() {
        console.log('onAISubmit');
        // Ensure state exists for the current sheet
        this.ensureSheetStateExists(this.sheetid);

        if (this.project.sheetStates[this.sheetid].isSubmitting) {
            this.abortSubmit();
            return;
        }
        console.log('onAISubmit 2');

        const requestSheetId = this.sheetid;
        console.log(`AI request initiated for sheetId: ${requestSheetId}`);
        // Ensure state exists for the request sheet
        this.ensureSheetStateExists(requestSheetId);

        try {
            // Update state directly
            this.project.sheetStates[requestSheetId].isSubmitting = true;
            this.project.sheetStates[requestSheetId].isLoading = true;
            this.project.sheetStates[requestSheetId].isProcessingResponse = true;

            // Set sheet state to running
            if (this.project.sheets && this.project.sheets[requestSheetId]) {
                this.project.sheets[requestSheetId].state = " [running]";
            }

            this.cdr.detectChanges(); // Trigger update

            const userPrompt = this.aiPrompt || '';

            this.ensureChatHistoryExists(requestSheetId);

            // Initialize expanded state when adding user message
            this.project.chatHistory[requestSheetId].push({ role: 'user', content: userPrompt, expanded: this.getMessageLineCount(userPrompt) <= 5 });
            this.aiPrompt = '';
            this.aiPromptChangee.emit('');
            this.chatHistoryChange.emit({ [requestSheetId]: this.project.chatHistory[requestSheetId] });
            this.scrollChatToBottom();

            console.log('onAISubmit 3');
            this.abortController = new AbortController();
            const signal = this.abortController.signal;

            console.log(`Using AI Provider: ${this.aiProvider}`);
            this.project.sheetStates[requestSheetId].loadingText = `Processing...`;
            this.cdr.detectChanges(); // Update text

            // ----- Start: Re-added API call logic ----- 
            let table = this.attachments.find(a => a.type === 'values' || a.type === 'formulas');
            let base64images = this.attachments.filter(a => a.type === 'image');
            // ---- ADDED: Filter for PDF documents ----
            let base64pdfs = this.attachments
                .filter(a => a.type === 'document' && a.fileType === 'pdf')
                .map(a => ({ data: a.data, fileName: a.fileName })); // Send data and filename
            // -------------------------------------------

            // Remove empty rows from table if it exists
            if (table) {
                let values = table.formulas;
                if (Array.isArray(values)) {
                    let lastNonEmptyRow = values.length - 1;
                    while (lastNonEmptyRow >= 0) {
                        const row = values[lastNonEmptyRow];
                        if (row.some(cell => cell !== "")) {
                            break;
                        }
                        lastNonEmptyRow--;
                    }
                    if (lastNonEmptyRow >= 0) {
                        table.formulas = values.slice(0, lastNonEmptyRow + 1);
                    }
                }
            }

            let systemPrompt;
            switch (this.aiProvider) {
                case 'claude': systemPrompt = SystemPrompt.claudePrompt; break;
                case 'gpt-4o': systemPrompt = SystemPrompt.openaiPrompt; break;
                case 'gpt-4.1': systemPrompt = SystemPrompt.openaiPrompt; break;
                case 'o3': systemPrompt = SystemPrompt.openaiPrompt; break;
                case 'gemini-2.5-flash': systemPrompt = SystemPrompt.geminiPrompt; break;
                case 'gemini-2.5-pro': systemPrompt = SystemPrompt.geminiPrompt; break;
                case 'deepseek': systemPrompt = SystemPrompt.deepseekPrompt; break;
                default: systemPrompt = SystemPrompt.claudePrompt;
            }

            if (table) {
                if (table.excelRange && table.excelRange.trim() !== "") {
                    systemPrompt += ` You are editing a table. You should focus on the range '${table.excelRange}'. The data for this specific range is: "${JSON.stringify(table.formulas)}". Please provide the edited data specifically for this range.`;
                } else {
                }
            }
            systemPrompt += ` The following table data is provided for editing: "${JSON.stringify(this.sheetformulas)}". Please provide the edited version of this data.`;

            if (base64images?.length > 0) { systemPrompt += ` You have ${base64images.length} image(s) of the model of the current situation.`; }
            const requestSheetChatHistory = this.project?.chatHistory?.[requestSheetId] || [];
            if (requestSheetChatHistory.length > 0) {
                systemPrompt += "\n\nHere is the conversation history so far:\n";
                requestSheetChatHistory.forEach(msg => { systemPrompt += `\n${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}\n`; });
                systemPrompt += "\nPlease continue the conversation based on this history.";
            }

            let endpoint = '';
            // Get current user ID for token tracking
            let userId: string | undefined;
            try {
                userId = await firstValueFrom(this.authService.authState.pipe(map(user => user?.uid)));
            } catch (error) {
                console.warn('Could not get user ID for token tracking:', error);
                userId = undefined;
            }

            let payload: any = { // Define payload type as any or a specific interface
                userInput: userPrompt,
                context: '',
                systemPrompt,
                base64images,
                table: JSON.stringify(table?.formulas),
                temperature: this.temperature,
                base64pdfs, // Add the filtered PDFs here
                userId: userId // Add userId for token tracking
            };

            switch (this.aiProvider) {
                case 'claude': endpoint = '/createClaudePrompt'; break;
                case 'gpt-4o':
                case 'gpt-4.1':
                case 'o4-mini':
                case 'o3':
                    endpoint = '/createOpenAIPrompt';
                    payload.modelName = this.aiProvider; // Add modelName for OpenAI
                    break;
                case 'gemini-2.5-flash':
                case 'gemini-2.5-flash-lite-preview-06-17':
                case 'gemini-2.5-pro':
                    endpoint = '/createGeminiPrompt';
                    payload.modelName = this.aiProvider; // Add modelName for Gemini
                    payload.thinkingEnabled = this.geminiThinkingEnabled; // Add thinking mode preference
                    break;
                case 'deepseek': endpoint = '/createDeepSeekPrompt'; break;
                default:
                    // Handle default or throw error?
                    console.error("Unsupported AI provider:", this.aiProvider);
                    this.toastr.error(`Unsupported AI provider selected: ${this.aiProvider}`, 'Error');
                    return; // Stop execution if provider is unknown
            }
            console.log('Making API request to:', endpoint);


            /// REST REQUEST --------------------
            const response = await fetch(this.apiUrl + endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload), // Use the payload object
                signal
            });

            if (!response.ok) {
                let errorData;
                try { errorData = await response.json(); } catch (jsonError) { throw new Error(`API request failed with status ${response.status}: ${response.statusText}`); }
                let errorMessage = 'API request failed';
                if (errorData.details) { errorMessage = errorData.details; } else if (errorData.error) { errorMessage = errorData.error; }
                // ... (add other error detail checks if needed) ...
                console.log("Error data:", errorData);
                throw new Error(errorMessage);
            }

            const data = await response.json();
            if (data.error) {
                let errorMessage = data.details || data.error;
                if (data.errorOrigin) { errorMessage = `API error: ${errorMessage}`; } // Example for specific origin
                throw new Error(errorMessage);
            }

            let rawResponseContent = data.response;

            // --- START: Strip JSON markdown fences for Gemini ---
            if (typeof rawResponseContent === 'string' && (this.aiProvider.startsWith('gemini-') || this.aiProvider.startsWith('o3'))) {
                let trimmedContent = rawResponseContent.trim(); // Trim whitespace first
                // Check if it starts with ```json\n and ends with ```
                if (trimmedContent.startsWith('```json\n') && trimmedContent.endsWith('\n```')) {
                    console.log('[AI Submit] Found Gemini JSON markers. Stripping...');
                    // Extract content between markers
                    trimmedContent = trimmedContent.substring('```json\n'.length, trimmedContent.length - '\n```'.length).trim();
                    rawResponseContent = trimmedContent; // Update the variable used later
                } else if (trimmedContent.startsWith('```') && trimmedContent.endsWith('```')) {
                    // Fallback: Handle cases with just ``` at start/end
                    console.log('[AI Submit] Found generic code fence markers. Stripping...');
                    trimmedContent = trimmedContent.substring('```'.length, trimmedContent.length - '```'.length).trim();
                    // If it looks like JSON after stripping, assume it was JSON
                    if (trimmedContent.startsWith('{') || trimmedContent.startsWith('[')) {
                        rawResponseContent = trimmedContent;
                    }
                }
            }
            // --- END: Strip JSON markdown fences ---

            if (typeof rawResponseContent === 'string') {
                const trimmedResponse = rawResponseContent.trim();
                // Check if it looks like a JSON array or object string AFTER potential stripping
                if ((trimmedResponse.startsWith('[') && trimmedResponse.endsWith(']')) || (trimmedResponse.startsWith('{') && trimmedResponse.endsWith('}'))) {
                    try {
                        let parsedResponse = JSON.parse(trimmedResponse);
                        // Ensure it's an array of arrays (existing validation) - Modify if object responses are expected
                        if (Array.isArray(parsedResponse)) {
                            for (let i = 0; i < parsedResponse.length; i++) { if (!Array.isArray(parsedResponse[i])) { parsedResponse[i] = [parsedResponse[i]]; } }
                        } else if (typeof parsedResponse === 'object' && parsedResponse !== null) {
                            // Handle potential object responses if needed, or keep validation strict to arrays
                            console.log('[AI Submit] Parsed JSON object response.');
                            // If only array of arrays is valid, throw error here
                            // throw new Error('Parsed response is not an array of arrays.');
                        } else {
                            throw new Error('Parsed response is not a valid JSON array or object.');
                        }

                        console.log(`[AI Submit] Parsed JSON response for sheet ${requestSheetId}.`);
                        // --- Existing JSON Handling ---
                        this.ensureChatHistoryExists(requestSheetId);
                        // Add a descriptive message instead of the raw JSON string
                        // Use the *original* raw response with markers for history if desired, or the stripped one
                        // Using the stripped one for cleaner history:
                        this.project.chatHistory[requestSheetId].push({ role: 'assistant', content: trimmedResponse, expanded: false });
                        this.chatHistoryChange.emit({ [requestSheetId]: this.project.chatHistory[requestSheetId] });
                        this.cdr.detectChanges();
                        this.scrollChatToBottom();

                        this.currentSheetData = parsedResponse;
                        this.updateHandsontableData(); // Update the handsontable view
                        this.copyTableData.emit({ data: this.currentSheetData, run: null, sheetId: requestSheetId }); // Emit data for parent component

                        // --- Agent Mode Logic (conditional on JSON response?) ---
                        if (this.agentMode && !signal.aborted && !this.aiAgentService.isRunning(requestSheetId)) {
                            console.log(`[AI Submit] Agent cycle check: AgentMode=${this.agentMode}, Aborted=${signal.aborted}, AgentRunning=${this.aiAgentService.isRunning(requestSheetId)}`);
                            const callbacks: AIAgentCallbacks = {
                                checkImageFn: this.testCheckImageContent.bind(this),
                                submitFn: this.agentSubmitContinuation.bind(this),
                                updateHistoryFn: this.addMessageToHistory.bind(this),
                                triggerChangeDetectionFn: this.cdr.detectChanges.bind(this.cdr),
                                setLoadingTextFn: this.setSheetLoadingText.bind(this),
                                getAgentModeFn: (id) => this.agentMode,
                                setAgentModeFn: (id, mode) => { this.agentMode = mode; this.cdr.detectChanges(); },
                                recreateModelFn: (id) => {
                                    return new Promise<void>((resolve) => {
                                        console.log(`Emitting recreateModelRequest for sheet ${id} with resolver`);
                                        this.recreateModelRequest.emit({ sheetId: id, resolve: resolve });
                                    });
                                }
                            };
                            // Pass raw string for now, agent might parse if needed or work with it directly
                            this.aiAgentService.startAgentCycle(requestSheetId, rawResponseContent, callbacks);
                        } else if (this.agentMode && signal.aborted) {
                            console.log(`[AI Submit] Agent mode was active but submit was aborted for sheet ${requestSheetId}. Stopping agent cycle.`);
                            this.stopAgent(requestSheetId);
                        } else if (this.aiAgentService.isRunning(requestSheetId)) {
                            console.log(`[AI Submit] Completed for sheet ${requestSheetId}, but agent is already running. Letting service continue.`);
                        }
                        // --- End Agent Mode Logic ---

                    } catch (parseError: any) {
                        console.error(`[AI Submit] Failed to parse expected JSON response for sheet ${requestSheetId}:`, parseError);
                        console.warn(`[AI Submit] Raw response was:`, trimmedResponse.substring(0, 500) + '...');
                        // Treat as text response, add raw string to history
                        this.ensureChatHistoryExists(requestSheetId);
                        this.project.chatHistory[requestSheetId].push({ role: 'assistant', content: `Received a response, but failed to parse it as a table:\n\n${trimmedResponse}`, expanded: true });
                        this.chatHistoryChange.emit({ [requestSheetId]: this.project.chatHistory[requestSheetId] });
                        this.cdr.detectChanges();
                        this.scrollChatToBottom();
                        this.toastr.warning('AI response received but could not be parsed as table data.', 'Parsing Warning');
                        // Do not update table, do not start agent
                    }
                } else {
                    // --- Handle General Text Response ---
                    console.log(`[AI Submit] Received general text response for sheet ${requestSheetId}.`);
                    this.ensureChatHistoryExists(requestSheetId);
                    // Add the raw text response directly to chat history
                    this.project.chatHistory[requestSheetId].push({ role: 'assistant', content: trimmedResponse, expanded: this.getMessageLineCount(trimmedResponse) <= 10 }); // Expand if <= 10 lines
                    this.chatHistoryChange.emit({ [requestSheetId]: this.project.chatHistory[requestSheetId] });
                    this.cdr.detectChanges();
                    this.scrollChatToBottom();
                    // Do not update table, do not start agent
                }
            } else {
                // Handle cases where data.response is not a string (unexpected)
                console.warn(`[AI Submit] Received non-string response content for sheet ${requestSheetId}:`, rawResponseContent);
                this.ensureChatHistoryExists(requestSheetId);
                this.project.chatHistory[requestSheetId].push({ role: 'assistant', content: `Received unexpected response format: ${JSON.stringify(rawResponseContent)}`, expanded: true });
                this.chatHistoryChange.emit({ [requestSheetId]: this.project.chatHistory[requestSheetId] });
                this.cdr.detectChanges();
                this.scrollChatToBottom();
                this.toastr.error('Received an unexpected response format from the AI.', 'Response Format Error');
            }
            // ----- End: Re-added API call logic ----- 

        } catch (error: any) {
            if (error.name === 'AbortError') {
                console.log('Fetch request aborted by user.');
                this.toastr.info('Request cancelled.', 'Aborted');
            } else {
                console.error('Error during AI submission:', error);
                // Simplified toastr call for brevity
                this.toastr.error(error.message || 'Failed to process request', 'AI Error', { timeOut: 8000 });
            }
        } finally {
            // Ensure state exists before resetting
            this.ensureSheetStateExists(requestSheetId);
            this.project.sheetStates[requestSheetId].isSubmitting = false;
            this.project.sheetStates[requestSheetId].isLoading = false;
            this.project.sheetStates[requestSheetId].isProcessingResponse = false;

            // Clear sheet state
            if (this.project.sheets && this.project.sheets[requestSheetId]) {
                this.project.sheets[requestSheetId].state = null;
            }

            this.abortController = null;

            this.cdr.detectChanges();
            console.log('onAISubmit finally block executed.');
        }
    }

    private abortCurrentRequest() {
        if (this.abortController) {
            console.log('Aborting current request');
            this.abortController.abort();
            this.abortController = null;
        }
    }

    abortSubmit() {
        this.project.sheetStates[this.sheetid].isSubmitting = false;
        this.abortCurrentRequest();
        this.toastr.warning('Submission stopped.');
    }

    async getPrompt(prompt: string) {
        if (!prompt) {
            this.toastr.info('Please enter a prompt first.');
            return;
        }

        this.isImprovingPrompt = true;
        this.cdr.detectChanges();

        const url = `${this.apiUrl}/createPromptExampleOpenAI`;
        try {
            const response: any = await this.http.post(url, { text: prompt }).toPromise();
            if (response && response.response) {
                // Update the aiPrompt model, which is bound to the textarea
                this.aiPrompt = response.response;
                // Since we're directly updating the model, Angular's change detection
                // should handle the textarea update. However, if using #promptTextarea
                // to manually set value, you might need to trigger change detection.
                //   this.toastr.success('Prompt improved successfully!');

                // Manually trigger change detection if needed, though ngModel should suffice
                this.cdr.detectChanges();

                // Focus and adjust height after a brief delay to ensure DOM is updated
                setTimeout(() => {
                    const textarea: HTMLTextAreaElement | undefined = this.promptTextareas.first?.nativeElement;
                    if (textarea) {
                        this.adjustTextareaHeight({ target: textarea });
                        textarea.focus();
                    }
                }, 0);
            } else {
                this.toastr.error('Failed to get a valid response from the AI.', 'Error');
            }
        } catch (error) {
            console.error('Error improving prompt:', error);
            this.toastr.error('An error occurred while improving the prompt.', 'Error');
        } finally {
            this.isImprovingPrompt = false;
            this.cdr.detectChanges();
        }
    }

    aiPromptChange(event: any) {
        this.aiPrompt = event;
        this.aiPromptChangee.emit(this.aiPrompt);
    }

    // ... (Methods like base64screenshotsChange, add2Sheet, addScreenshot, etc. remain the same)

    async testCheckImageContent(sheetId: number = this.sheetid): Promise<string | null> {
        this.ensureSheetStateExists(sheetId); // Ensure state exists at the start

        // Check added in handleCheckViewButtonClick, but double-check doesn't hurt
        if (this.project?.sheetStates?.[sheetId]?.isCheckingImage) {
            console.warn(`[Check Image] Attempted to start check for sheet ${sheetId}, but already running.`);
            // Avoid toastr here as handleCheckViewButtonClick might handle abort message
            return null;
        }

        console.log(`[Check Image] Initiated for sheetId: ${sheetId}`);
        const checkAbortController = new AbortController(); // Create a fresh controller
        this.checkAbortController = checkAbortController; // Store it (overwrites previous, maybe needs per-sheet)
        const signal = checkAbortController.signal;

        try {
            // Ensure state exists before modifying
            if (!this.project?.sheetStates?.[sheetId]) {
                throw new Error(`Sheet state for ${sheetId} is missing.`);
            }
            this.project.sheetStates[sheetId].isCheckingImage = true;
            this.animatedCheckButtonActive = true; // Keep general animation for now
            this.cdr.detectChanges(); // Update UI immediately

            this.ensureChatHistoryExists(sheetId);
            const checkMessage = "Checking 3D model in current view...";
            this.project.chatHistory[sheetId].push({ role: 'user', content: checkMessage, expanded: false });
            this.chatHistoryChange.emit({ [sheetId]: this.project.chatHistory[sheetId] });
            this.scrollChatToBottom();
            // Don't detectChanges here, let finally handle it

            this.renderer.render(this.scene, this.camera);

            // 1. Promisify the blob capture
            console.log(`[Check Image] Capturing blob for sheet ${sheetId}...`);
            const blob = await new Promise<Blob>((resolveBlob, rejectBlob) => {
                this.renderer.domElement.toBlob(blob => {
                    if (signal.aborted) {
                        rejectBlob(new DOMException('Aborted during blob capture', 'AbortError'));
                        return;
                    }
                    if (blob) resolveBlob(blob);
                    else rejectBlob(new Error('Canvas to Blob conversion failed'));
                }, 'image/png'); // Specify format if needed
            });
            console.log(`[Check Image] Blob captured for sheet ${sheetId}. Size: ${blob.size}`);

            if (signal.aborted) {
                console.log('[Check Image] Aborted by user after blob capture');
                this.manualCheckResult$.next({ sheetId, result: null }); // Still emit null for subscription
                return null;
            }

            // 2. Promisify the FileReader
            console.log(`[Check Image] Reading blob as data URL for sheet ${sheetId}...`);
            const base64data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                // Abort handling within callbacks
                const abortHandler = () => reject(new DOMException('Aborted during file read', 'AbortError'));
                signal.addEventListener('abort', abortHandler, { once: true });

                reader.onloadend = () => {
                    signal.removeEventListener('abort', abortHandler); // Cleanup listener
                    if (reader.result) {
                        resolve(reader.result as string);
                    } else {
                        reject(new Error('FileReader finished with no result.'));
                    }
                };
                reader.onerror = (error) => {
                    signal.removeEventListener('abort', abortHandler); // Cleanup listener
                    reject(new Error(`FileReader error: ${error}`));
                };
                reader.readAsDataURL(blob);
            });
            console.log(`[Check Image] Blob read completed for sheet ${sheetId}. Data length: ${base64data.length}`);

            if (signal.aborted) {
                console.log('[Check Image] Aborted by user after file read');
                this.manualCheckResult$.next({ sheetId, result: null }); // Still emit null for subscription
                return null;
            }

            // 3. Prepare prompt
            const historyForPrompt = this.project.chatHistory?.[sheetId]?.map((msg: ChatMessage) => `${msg.role}: ${msg.content}`).join('\n') || 'No history';
            const promptText = `Analyze the 3D model in the provided image based on the following conversation history. Does the model accurately reflect the user's requests and the generated spreadsheet data? Identify discrepancies or confirm accuracy.\n\nConversation History:\n${historyForPrompt}\n\nLatest User Prompt (leading to this view): ${this.aiPrompt || 'Not available'}`;

            // 4. Get current user ID for token tracking
            let userId: string | undefined;
            try {
                userId = await firstValueFrom(this.authService.authState.pipe(map(user => user?.uid)));
            } catch (error) {
                console.warn('Could not get user ID for token tracking in image analysis:', error);
                userId = undefined;
            }

            // 5. Fetch API
            console.log(`[Check Image] Making API call to checkImageContentGemini for sheet ${sheetId}`);
            const response = await fetch(`${this.apiUrl}/checkImageContentGemini`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    imageData: base64data,
                    promptText: promptText,
                    objectParam: "3D model",
                    userId: userId // Add userId for token tracking
                    // Add modelName if needed: modelName: 'gemini-2.5-pro-preview-03-25' // Example
                }),
                signal // Pass the signal here
            });
            console.log(`[Check Image] API call completed for sheet ${sheetId}, status: ${response.status}`);

            if (signal.aborted) {
                console.log('[Check Image] Aborted by user during fetch');
                this.manualCheckResult$.next({ sheetId, result: null }); // Still emit null for subscription
                return null;
            }

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                    console.error("[Check Image] API Error Response Body:", errorData);
                } catch (e) {
                    errorData = { message: await response.text() }; // Get text if JSON parsing fails
                    console.error("[Check Image] API Error Response Text:", errorData.message);
                }
                throw new Error(`API request failed with status ${response.status}: ${errorData?.details || errorData?.message || response.statusText}`);
            }

            // 5. Process result
            const result = await response.json();
            console.log(`[Check Image] API successful. Result for sheet ${sheetId}:`, result);

            if (!result || !result.analysis) {
                console.warn(`[Check Image] API response missing 'analysis' field for sheet ${sheetId}. Response:`, result);
                throw new Error("Invalid API response format: missing 'analysis' field.");
            }

            const resultString = JSON.stringify(result.analysis, null, 2);
            console.log(`[Check Image] Success. Returning resultString for sheet ${sheetId}.`);
            this.manualCheckResult$.next({ sheetId, result: resultString }); // Keep emitting result via Subject for potential separate listeners
            return resultString;

        } catch (error: any) {
            // Centralized error handling
            if (error.name === 'AbortError') {
                console.log(`[Check Image] Aborted operation for sheet ${sheetId}: ${error.message}`);
                // Avoid toastr here, let caller handle user feedback for abort if needed
            } else {
                console.error(`[Check Image] Error during execution for sheet ${sheetId}:`, error);
                this.toastr.error(`Error checking image: ${error.message || 'Unknown error'}`, 'Analysis Error', { timeOut: 8000 });
                const errorMessage = `Error during image check: ${error.message || 'Unknown error'}`;
                // Add error to chat history
                try {
                    this.ensureChatHistoryExists(sheetId);
                    this.project.chatHistory[sheetId].push({ role: 'assistant', content: errorMessage, expanded: true });
                    this.chatHistoryChange.emit({ [sheetId]: this.project.chatHistory[sheetId] });
                    this.scrollChatToBottom();
                } catch (historyError) {
                    console.error(`[Check Image] Failed to add error to history for sheet ${sheetId}:`, historyError);
                }
            }
            this.manualCheckResult$.next({ sheetId, result: null }); // Emit null on error/abort via Subject
            return null;
        } finally {
            console.log(`[Check Image] Entering finally block for sheet ${sheetId}`);
            // Reset checking state reliably
            if (this.project?.sheetStates?.[sheetId]) { // Add null checks
                this.project.sheetStates[sheetId].isCheckingImage = false;
            }
            this.animatedCheckButtonActive = Object.values(this.project?.sheetStates || {}).some((state: any) => state?.isCheckingImage); // Add null checks
            if (this.checkAbortController === checkAbortController) {
                this.checkAbortController = null; // Clear controller if it's the one we created
            }
            console.log(`[Check Image] State reset for sheet ${sheetId}. isCheckingImage: ${this.project?.sheetStates?.[sheetId]?.isCheckingImage}, animatedCheckButtonActive: ${this.animatedCheckButtonActive}`);
            // Run change detection within finally to ensure UI updates AFTER state reset
            this.zone.run(() => {
                this.cdr.detectChanges();
                console.log(`[Check Image] Change detection triggered in finally block for sheet ${sheetId}`);
            });
        }
    }

    // Method to handle the click event from the "Check View" button
    handleCheckViewButtonClick() {
        const sheetId = this.sheetid;
        console.log(`[Manual Check] Button clicked for sheet ${sheetId}. Component sheetId: ${this.sheetid}`);

        if (!this.project || !this.project.sheetStates || !this.project.sheetStates[sheetId]) {
            console.error(`[Manual Check] Error: Project or sheet state not initialized for sheetId: ${sheetId}`);
            this.toastr.error('Internal state error. Cannot perform check.', 'Error');
            return;
        }

        if (this.project.sheetStates[sheetId].isCheckingImage) {
            console.log(`[Manual Check] Abort requested as check is already running for sheet ${sheetId}`);
            this.abortCheckRequest(sheetId);
            return;
        }

        this.project.sheetStates[sheetId].loadingText = `Checking view...`;
        this.cdr.detectChanges();

        console.log(`[Manual Check] ==> Calling testCheckImageContent (will emit result via Subject) for sheet ${sheetId}`);
        // Simply call the function - result handling is done in the Subject subscription
        this.testCheckImageContent(sheetId);
        console.log(`[Manual Check] testCheckImageContent called for sheet ${sheetId}. Waiting for result via Subject...`);
    }

    private async agentSubmitContinuation(sheetId: number, nextPrompt: string): Promise<void> {
        console.log(`Agent continuation: Submitting for sheet ${sheetId}`);
        // Ensure the state is ready for a new submission
        this.ensureSheetStateExists(sheetId);
        if (this.project.sheetStates[sheetId]) {
            this.project.sheetStates[sheetId].isSubmitting = false;
            this.project.sheetStates[sheetId].isLoading = false;
            this.project.sheetStates[sheetId].isProcessingResponse = false;
        }

        // Set the prompt generated by the agent
        this.aiPrompt = nextPrompt;
        this.aiPromptChangee.emit(this.aiPrompt);

        // Clear attachments for the next agent iteration? Decide based on desired behavior.
        // this.attachments = []; 

        // Now call the main submit function
        await this.onAISubmit();
    }

    private stopAgent(sheetId: number): void {
        if (this.aiAgentService.isRunning(sheetId)) {
            const callbacks: AIAgentCallbacks = {
                checkImageFn: this.testCheckImageContent.bind(this),
                submitFn: this.agentSubmitContinuation.bind(this),
                updateHistoryFn: this.addMessageToHistory.bind(this),
                triggerChangeDetectionFn: this.cdr.detectChanges.bind(this.cdr),
                setLoadingTextFn: this.setSheetLoadingText.bind(this),
                getAgentModeFn: (id) => this.agentMode,
                setAgentModeFn: (id, mode) => { this.agentMode = mode; this.cdr.detectChanges(); },
                recreateModelFn: (id) => {
                    return new Promise<void>((resolve) => {
                        console.log(`Emitting recreateModelRequest from stopAgent for sheet ${id} with resolver`);
                        this.recreateModelRequest.emit({ sheetId: id, resolve: resolve });
                    });
                }
            };
            this.aiAgentService.stopAgentCycle(sheetId, callbacks);
            this.agentMode = false; // Ensure component state reflects stopped agent
            this.cdr.detectChanges();
        }
    }

    private addMessageToHistory(sheetId: number, message: ChatMessage): void {
        this.ensureChatHistoryExists(sheetId);
        this.project.chatHistory[sheetId].push(message);
        this.chatHistoryChange.emit({ [sheetId]: this.project.chatHistory[sheetId] });
        this.scrollChatToBottom(); // Scroll after adding
    }

    private setSheetLoadingText(sheetId: number, text: string): void {
        this.ensureSheetStateExists(sheetId);
        if (this.project.sheetStates[sheetId]) {
            this.project.sheetStates[sheetId].loadingText = text;
        }
    }

    private abortCheckRequest(sheetId: number = this.sheetid) { // Accept optional sheetId
        // Find the controller associated with the check for this sheetId if stored per-sheet
        // For now, using the single shared controller:
        if (this.checkAbortController) {
            console.log(`Aborting check image request potentially for sheet ${sheetId}`);
            this.checkAbortController.abort();
            this.checkAbortController = null; // Clear the shared controller

            // Ensure state exists before checking/resetting
            this.ensureSheetStateExists(sheetId);
            if (this.project.sheetStates[sheetId]) {
                this.project.sheetStates[sheetId].isCheckingImage = false;
                this.project.sheetStates[sheetId].loadingText = ''; // Clear loading text
            }
            this.animatedCheckButtonActive = Object.values(this.project.sheetStates || {}).some((state: any) => state.isCheckingImage);
            this.cdr.detectChanges();
            this.toastr.info('Image check aborted.', 'Aborted');
        } else {
            console.log(`No active check request found to abort for sheet ${sheetId}`);
        }
    }

    // ... (diff methods remain the same)

    copyAssistantResponseToSheet(content: string, messageIndex: number): void {
        // ... (This method remains largely the same, just emitting with this.sheetid)
        const targetSheetId = this.sheetid;
        console.log(`Copying assistant response to sheetId: ${targetSheetId}`);

        try {
            let parsedData = JSON.parse(content);
            if (!Array.isArray(parsedData)) {
                throw new Error('Parsed data is not an array.');
            }
            console.log('Copying assistant response to sheet:', parsedData);
            this.copyTableData.emit({ data: parsedData, run: null, sheetId: targetSheetId });

            this.animatedSetButtonActive = true;
            this.animatedSetButtonMessageIndex = messageIndex;

            setTimeout(() => {
                if (this.animatedSetButtonMessageIndex === messageIndex) {
                    this.animatedSetButtonActive = false;
                    this.animatedSetButtonMessageIndex = -1;
                }
                this.cdr.detectChanges();
            }, 3000);

        } catch (error) {
            console.error('Error parsing or copying assistant response:', error, content);
            this.toastr.error('Failed to parse or copy assistant response. Ensure it is valid JSON.', 'Copy Failed');
        }
    }

    copyToPromptAndSubmit(content: string): void {
        try {
            this.aiPrompt = content;
            this.aiPromptChangee.emit(this.aiPrompt);

            const textareaElement = this.promptTextareas?.first?.nativeElement;
            if (textareaElement) {
                textareaElement.value = content;
                textareaElement.style.height = 'auto';
                textareaElement.style.height = `${textareaElement.scrollHeight}px`;
            }

            // Ensure state exists before checking
            this.ensureSheetStateExists(this.sheetid);
            if (!this.project.sheetStates[this.sheetid].isSubmitting) {
                this.onAISubmit();
            } else {
                this.toastr.warning('Already processing a request. Please wait or cancel the current request.', 'Operation in Progress');
            }

            this.toastr.success('Content copied to prompt and submitted', 'Success');
        } catch (error) {
            console.error('Error copying to prompt and submitting:', error);
            this.toastr.error('Failed to copy content to prompt and submit', 'Error');
        }
    }

    // ... (getAttachmentDisplay remains the same)

    public setActiveSheet(sheetId: number): void {
        console.log(`AI Editor: Setting active sheet to: ${sheetId}`);
        if (this.sheetid === sheetId) {
            console.log(`AI Editor: Sheet ${sheetId} is already active.`);
            return;
        }

        // Abort any ongoing AI requests *associated with the sheet we are leaving*
        // This requires knowing which sheet the request belongs to, which is tricky
        // without a more robust request tracking system. For simplicity, we'll keep aborting
        // based on the *currently active* abort controllers.
        this.abortSubmit();
        this.abortCheckRequest();
        this.abortAnalyzeRequest();

        this.sheetid = sheetId;

        // Ensure state and history exist for the new sheet
        this.ensureChatHistoryExists(sheetId);
        this.ensureSheetStateExists(sheetId);

        // Reset UI elements not tied to sheet state
        this.aiPrompt = '';
        this.attachments = [];
        this.currentSheetData = this.generateRandomData(5, 5);
        this.diffIndex = -1;
        this.showDiff = false;
        this.animatedSetButtonActive = false;
        this.animatedCheckButtonActive = false;

        // Emit history change for the *new* sheet
        this.chatHistoryChange.emit({ [this.sheetid]: this.project.chatHistory[this.sheetid] });
        this.scrollChatToBottom();
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        this.appRef.tick();
        console.log("AI Editor: State reset and history structure ensured for sheet", sheetId);

        this.updateHandsontableData();
        this.scrollChatToBottom();
        this.cdr.detectChanges();
    }


    trackByFn(index: number, item: ChatMessage): any {
        // Use index as the unique identifier
        return index; // Using index is usually sufficient unless items are reordered/deleted frequently
    }

    async analyzeModel() {
        // Ensure state exists
        this.ensureSheetStateExists(this.sheetid);

        if (this.project.sheetStates[this.sheetid].isAnalyzingModel) {
            this.abortAnalyzeRequest();
            return;
        }

        const analyzeSheetId = this.sheetid;
        // Ensure state exists
        this.ensureSheetStateExists(analyzeSheetId);

        try {
            this.project.sheetStates[analyzeSheetId].isAnalyzingModel = true;
            this.cdr.detectChanges();

            this.analyzeAbortController = new AbortController();
            const signal = this.analyzeAbortController.signal;

            // Re-add the core logic here
            this.ensureChatHistoryExists(analyzeSheetId);
            this.project.chatHistory[analyzeSheetId].push({ role: 'user', content: "Analyzing 3D model structure...", expanded: false });
            this.scrollChatToBottom();
            this.cdr.detectChanges();

            const sceneData = this.prepareSceneData();
            this.renderer.render(this.scene, this.camera);
            const blob = await new Promise<Blob>(resolve => this.renderer.domElement.toBlob(resolve));
            const base64data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
            });

            if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

            const response = await fetch(`${this.apiUrl}/analyzeModel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ imageData: base64data, sceneData: sceneData }),
                signal
            });

            if (!response.ok) {
                let errorData;
                try { errorData = await response.json(); } catch (e) { errorData = { message: response.statusText }; }
                throw new Error(`API request failed with status ${response.status}: ${errorData?.message || response.statusText}`);
            }
            const analysisResult = await response.json();
            console.log('Model analysis result:', analysisResult);

            // Format and add result to history
            let formattedResult = this.formatAnalysisResult(analysisResult);
            this.ensureChatHistoryExists(analyzeSheetId);
            this.project.chatHistory[analyzeSheetId].push({ role: 'assistant', content: formattedResult, expanded: false });
            this.chatHistoryChange.emit({ [analyzeSheetId]: this.project.chatHistory[analyzeSheetId] });
            this.scrollChatToBottom();
            this.toastr.success('Model analysis complete', 'Analysis Complete');

        } catch (error: any) {
            // Push error to chat history
            if (error.name === 'AbortError') {
                this.toastr.info('Model analysis cancelled.', 'Aborted');
            } else {
                console.error('Error analyzing model:', error);
                this.toastr.error(`Error analyzing model: ${error.message}`, 'Analysis Error');
                this.ensureChatHistoryExists(analyzeSheetId);
                this.project.chatHistory[analyzeSheetId].push({ role: 'assistant', content: `Error analyzing model: ${error.message || 'Unknown error'}`, expanded: false });
                this.chatHistoryChange.emit({ [analyzeSheetId]: this.project.chatHistory[analyzeSheetId] });
                this.scrollChatToBottom();
            }
        } finally {
            // Ensure state exists
            this.ensureSheetStateExists(analyzeSheetId);
            this.project.sheetStates[analyzeSheetId].isAnalyzingModel = false;
            this.analyzeAbortController = null;
            this.cdr.detectChanges();
        }
    }

    private abortAnalyzeRequest() {
        if (this.analyzeAbortController) {
            console.log('Aborting analyze model request');
            this.analyzeAbortController.abort();
            this.analyzeAbortController = null;

            // Reset state for current sheet
            this.ensureSheetStateExists(this.sheetid);
            this.project.sheetStates[this.sheetid].isAnalyzingModel = false;
            this.cdr.detectChanges();
        }
    }

    scrollChatToBottom(): void {
        setTimeout(() => {
            if (this.chatHistoryContainer && this.chatHistoryContainer.nativeElement) {
                const element = this.chatHistoryContainer.nativeElement;
                element.scrollTop = element.scrollHeight;
            }
        }, 100);
    }

    handleKeyDown(event: KeyboardEvent): void {
        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
            event.preventDefault();

            // Ensure state exists
            this.ensureSheetStateExists(this.sheetid);
            if (!this.project.sheetStates[this.sheetid].isSubmitting) {
                this.onAISubmit();
            }
        }
    }

    shouldShowSetAnimation(messageIndex: number): boolean {
        // This controls the checkmark animation on the "set" button
        return this.animatedSetButtonActive && this.animatedSetButtonMessageIndex === messageIndex;
    }

    toggleDiff(index: number): void {
        if (this.diffIndex === index) {
            this.showDiff = !this.showDiff; // Toggle if clicking the same
        } else {
            this.diffIndex = index;
            this.showDiff = true; // Show diff for the new index
        }
        this.cdr.detectChanges();
    }

    getDiffHtml(currentIndex: number): string {
        const currentSheetHistory = this.project?.chatHistory?.[this.sheetid];
        if (currentIndex <= 0 || !currentSheetHistory || !currentSheetHistory[currentIndex - 2] || currentSheetHistory[currentIndex]?.role !== 'assistant' || currentSheetHistory[currentIndex - 2]?.role !== 'assistant') {
            return this.escapeHtml(currentSheetHistory?.[currentIndex]?.content || '');
        }

        const previousContent = currentSheetHistory[currentIndex - 2].content;
        const currentContent = currentSheetHistory[currentIndex].content;
        const delta = this.diffPatcher.diff(previousContent, currentContent);

        if (!delta) {
            return '<div class="no-changes">No changes detected</div>';
        }

        return this.createTextDiff(previousContent, currentContent);
    }

    createTextDiff(oldText: string, newText: string): string {
        const oldLines = (oldText || '').split('\n');
        const newLines = (newText || '').split('\n');
        let diffHtml = '';
        const maxLines = Math.max(oldLines.length, newLines.length);

        for (let i = 0; i < maxLines; i++) {
            const oldLine = i < oldLines.length ? oldLines[i] : null;
            const newLine = i < newLines.length ? newLines[i] : null;

            if (oldLine === newLine) {
                diffHtml += `<div class="diff-line unchanged">${this.escapeHtml(newLine || '')}</div>`;
            } else if (oldLine !== null && newLine === null) {
                diffHtml += `<div class="diff-line removed">- ${this.escapeHtml(oldLine)}</div>`;
            } else if (oldLine === null && newLine !== null) {
                diffHtml += `<div class="diff-line added">+ ${this.escapeHtml(newLine)}</div>`;
            } else {
                // Use highlightCharacterDifferences for changed lines
                diffHtml += this.highlightCharacterDifferences(oldLine || '', newLine || '');
            }
        }
        return diffHtml;
    }

    highlightCharacterDifferences(oldLine: string, newLine: string): string {
        // ... (This existing diff logic can remain) ...
        let html = '';
        let i = 0;
        while (i < oldLine.length && i < newLine.length && oldLine[i] === newLine[i]) { i++; }
        let j = 0;
        while (j < oldLine.length - i && j < newLine.length - i && oldLine[oldLine.length - 1 - j] === newLine[newLine.length - 1 - j]) { j++; }
        const commonPrefix = oldLine.substring(0, i);
        const commonSuffix = j > 0 ? oldLine.substring(oldLine.length - j) : '';
        const oldDiffPart = oldLine.substring(i, oldLine.length - j);
        const newDiffPart = newLine.substring(i, newLine.length - j);

        html += '<div class="diff-line removed">- ';
        html += this.escapeHtml(commonPrefix);
        html += `<span class="highlight-removed">${this.escapeHtml(oldDiffPart)}</span>`;
        html += this.escapeHtml(commonSuffix);
        html += '</div>';
        html += '<div class="diff-line added">+ ';
        html += this.escapeHtml(commonPrefix);
        html += `<span class="highlight-added">${this.escapeHtml(newDiffPart)}</span>`;
        html += this.escapeHtml(commonSuffix);
        html += '</div>';
        return html;
    }

    escapeHtml(text: string): string {
        if (!text) return '';
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
    }

    adjustTextareaHeight(event: any): void {
        const textarea = event.target;
        textarea.style.height = 'auto'; // Reset height
        textarea.style.height = textarea.scrollHeight + 'px'; // Set to content height
    }

    async addTableMarking(type: string) {
        this.tableMarkingType = type;
        this.tableMarkingDropdown = false; // Close dropdown
        const tableMarking = {
            type: type,
            markingType: this.tableMarkingType,
            index: this.attachments.length,
            timestamp: new Date().toISOString(),
            excelRange: "",
            selectedRange: null,
            formulas: null,
            values: null
        };

        if (this.hotInstance) {
            const selectedCells = this.hotInstance.getSelected();
            if (selectedCells && selectedCells.length > 0) {
                const [startRow, startCol, endRow, endCol] = selectedCells[0];
                const selectedRange = {
                    start: { sheet: this.sheetid, col: startCol, row: startRow },
                    end: { sheet: this.sheetid, col: endCol, row: endRow }
                };
                try {
                    var cellstart = await this.excels[this.PROJECT].simpleCellAddressToString({ sheet: this.sheetid, row: startRow, col: startCol }, this.sheetid);
                    var cellend = await this.excels[this.PROJECT].simpleCellAddressToString({ sheet: this.sheetid, row: endRow, col: endCol }, this.sheetid);
                    tableMarking.excelRange = cellstart + ":" + cellend;
                    tableMarking.selectedRange = selectedRange;
                    tableMarking.formulas = await this.excels[this.PROJECT].getRangeSerialized(selectedRange);
                    tableMarking.values = await this.excels[this.PROJECT].getRangeValues(selectedRange);
                    console.log('Selected cells tableMarking:', tableMarking);
                } catch (e) {
                    console.error("Error getting range data:", e);
                    this.toastr.error("Could not get data for the selected range.", "Error");
                    return; // Don't add the attachment if data retrieval failed
                }
            } else {
                this.toastr.info("No cells selected in the table.", "Info");
                return; // Don't add if nothing is selected
            }
        } else {
            // Get full sheet data if no selection or HotInstance unavailable?
            // For now, we require a selection.
            console.warn('Handsontable instance not found or no cells selected for adding table marking.');
            this.toastr.warning("Please select a range in the table first.", "Selection Required");
            return; // Don't add if table isn't ready or no selection
        }

        this.attachments.push(tableMarking);
        console.log('Table marking added:', tableMarking);
        this.cdr.detectChanges();
    }

    async addScreenshot(typ: string = 'model') { // Default type
        this.screenshotDropdown = false; // Close dropdown
        const newscreenshot = {
            type: typ,
            isScreenshot: true,
            index: this.attachments.length,
            data: '', // Initialize data
            timestamp: new Date().toISOString()
        };
        try {
            this.renderer.render(this.scene, this.camera);
            const blob = await new Promise<Blob>((resolve, reject) => {
                this.renderer.domElement.toBlob(blob => {
                    if (blob) resolve(blob);
                    else reject(new Error('Canvas to Blob conversion failed'));
                });
            });
            const base64data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
            newscreenshot.data = base64data;
            this.attachments.push(newscreenshot);
            console.log('Screenshot added:', newscreenshot);
            this.cdr.detectChanges();
        } catch (error) {
            console.error('Screenshot failed:', error);
            this.toastr.error('Failed to capture screenshot.', 'Screenshot Error');
        }
    }

    addImage() {
        this.screenshotDropdown = false; // Close dropdown
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (event: any) => {
            const file = event.target.files?.[0]; // Optional chaining
            if (file) {
                const reader = new FileReader();
                reader.onload = (e: any) => {
                    const imageData = e.target.result;
                    const imageAttachment = {
                        data: imageData,
                        type: 'image', // Consistent type
                        markingType: 'uploaded',
                        index: this.attachments.length,
                        timestamp: new Date().toISOString(),
                        excelRange: "",
                        selectedRange: null,
                        formulas: null,
                        values: null,
                        fileName: file.name // Store filename
                    };
                    this.attachments.push(imageAttachment);
                    console.log('Uploaded image added:', imageAttachment);
                    this.cdr.detectChanges();
                };
                reader.onerror = (error) => {
                    console.error('File reading error:', error);
                    this.toastr.error('Failed to read image file.', 'File Error');
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
    }

    addDocument() {
        this.screenshotDropdown = false; // Close dropdown
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf,.txt'; // Accept PDF and TXT files
        input.onchange = (event: any) => {
            const file = event.target.files?.[0]; // Optional chaining
            if (file) {
                const reader = new FileReader();
                reader.onload = (e: any) => {
                    const fileData = e.target.result;
                    const fileExtension = file.name.split('.').pop()?.toLowerCase();
                    const documentAttachment = {
                        data: fileData,
                        type: 'document', // New type for documents
                        fileType: fileExtension, // 'pdf' or 'txt'
                        markingType: 'uploaded',
                        index: this.attachments.length,
                        timestamp: new Date().toISOString(),
                        excelRange: "",
                        selectedRange: null,
                        formulas: null,
                        values: null,
                        fileName: file.name // Store filename
                    };
                    this.attachments.push(documentAttachment);
                    console.log(`Uploaded ${fileExtension} document added:`, documentAttachment);
                    this.toastr.success(`${file.name} attached successfully.`, 'Document Attached');
                    this.cdr.detectChanges();
                };
                reader.onerror = (error) => {
                    console.error('File reading error:', error);
                    this.toastr.error('Failed to read document file.', 'File Error');
                };

                // Use different reading methods based on file type
                if (file.name.toLowerCase().endsWith('.txt')) {
                    reader.readAsText(file); // Read as text for TXT files
                } else {
                    reader.readAsDataURL(file); // Read as data URL for PDF files
                }
            }
        };
        input.click();
    }

    removeScreenshot(index: number) { // Can remove any attachment by index
        if (index >= 0 && index < this.attachments.length) {
            const removed = this.attachments.splice(index, 1);
            console.log('Attachment removed at index:', index, removed);
            this.cdr.detectChanges();
        } else {
            console.warn('Attempted to remove attachment with invalid index:', index);
        }
    }

    getAttachmentDisplay(attachment: any): string {
        if (!attachment) return 'Invalid Attachment';
        const type = attachment.type;
        const timestamp = attachment.timestamp ? new Date(attachment.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

        if (attachment.isScreenshot) {
            if (attachment.markingType === 'multi-view' && attachment.viewName) {
                // Format camera positions for readability
                const pos = attachment.cameraPosition ?
                    `(${attachment.cameraPosition.map((v: number) => v.toFixed(0)).join(', ')})` : '';
                const dir = attachment.cameraDirection ?
                    `→ (${attachment.cameraDirection.map((v: number) => v.toFixed(0)).join(', ')})` : '';

                return `📷 ${attachment.viewName} View ${pos} ${dir} (${timestamp})`;
            }
            return `📷 Screenshot (${timestamp})`;
        } else if (type === 'image' && attachment.markingType === 'uploaded') {
            return `🖼️ ${attachment.fileName || 'Uploaded Image'} (${timestamp})`;
        } else if (type === 'document') {
            const icon = attachment.fileType === 'pdf' ? '📑' : '📄';
            return `${icon} ${attachment.fileName || 'Document'} (${timestamp})`;
        } else if (type === 'values' || type === 'formulas') {
            const range = attachment.excelRange || 'Full Sheet';
            return `📊 Table (${type}: ${range}) (${timestamp})`;
        } else {
            return `📎 Unknown Attachment (${timestamp})`;
        }
    }

    private prepareSceneData(): any {
        // Prepare basic scene data for model analysis
        try {
            const sceneData = {
                objects: [],
                stats: {
                    objectCount: 0,
                    totalVertices: 0,
                    totalTriangles: 0
                }
            };

            if (this.scene) {
                // Traverse scene to gather object information
                this.scene.traverse((object: any) => {
                    if (object.isMesh) {
                        const objectData = {
                            name: object.name || 'Unnamed Object',
                            type: object.type || 'Mesh',
                            position: object.position ? [object.position.x, object.position.y, object.position.z] : null,
                            geometry: {
                                type: object.geometry ? object.geometry.type : 'Unknown',
                                vertices: object.geometry ? (object.geometry.attributes.position ? object.geometry.attributes.position.count : 0) : 0
                            }
                        };

                        sceneData.objects.push(objectData);
                        sceneData.stats.objectCount++;

                        if (object.geometry) {
                            if (object.geometry.attributes && object.geometry.attributes.position) {
                                sceneData.stats.totalVertices += object.geometry.attributes.position.count;
                            }
                            if (object.geometry.index) {
                                sceneData.stats.totalTriangles += object.geometry.index.count / 3;
                            } else if (object.geometry.attributes && object.geometry.attributes.position) {
                                // For non-indexed geometry, triangle count is vertex count / 3
                                sceneData.stats.totalTriangles += object.geometry.attributes.position.count / 3;
                            }
                        }
                    }
                });
            }

            return sceneData;
        } catch (error) {
            console.error('Error preparing scene data:', error);
            return { objects: [], stats: { objectCount: 0, totalVertices: 0, totalTriangles: 0 } };
        }
    }

    private formatAnalysisResult(analysisResult: any): string {
        // Helper to format the analysis result for chat history
        let formattedResult = '### 3D Model Analysis\n\n';
        if (!analysisResult) return 'Analysis completed, but no detailed information returned.';

        if (analysisResult.stats) {
            formattedResult += '**Scene Statistics:**\n';
            formattedResult += `- Total Objects: ${analysisResult.stats.objectCount || 'N/A'}\n`;
            formattedResult += `- Total Vertices: ${analysisResult.stats.totalVertices || 'N/A'}\n`;
            formattedResult += `- Total Triangles: ${analysisResult.stats.totalTriangles || 'N/A'}\n\n`;
        }
        if (analysisResult.suggestions && analysisResult.suggestions.length > 0) {
            formattedResult += '**Suggestions:**\n';
            analysisResult.suggestions.forEach((suggestion: any, index: number) => {
                formattedResult += `${index + 1}. **${suggestion.type?.toUpperCase()}**: ${suggestion.message}\n`;
                if (suggestion.explanation) { formattedResult += `   *${suggestion.explanation}*\n`; }
                if (suggestion.suggestedValue) { formattedResult += `   Suggested value: \`${suggestion.suggestedValue}\`\n`; }
            });
            formattedResult += '\n';
        } else {
            formattedResult += '**No specific suggestions found.**\n\n';
        }
        if (analysisResult.objects && analysisResult.objects.length > 0) {
            formattedResult += '**Objects Summary:**\n';
            analysisResult.objects.forEach((obj: any, index: number) => {
                formattedResult += `${index + 1}. ${obj.name || 'Object ' + (index + 1)}\n`;
                formattedResult += `   - Type: ${obj.type || 'N/A'}\n`;
                if (obj.position) { formattedResult += `   - Position: (${obj.position.map((v: number) => v.toFixed(2)).join(', ')})\n`; }
                if (obj.geometry) { formattedResult += `   - Geometry: ${obj.geometry.type || 'N/A'} (${obj.geometry.vertices || 0} vertices)\n`; }
            });
        }
        return formattedResult;
    }

    // Helper method to count lines in a message
    public getMessageLineCount(messageContent: string): number {
        // Split by newline characters and return the count.
        // Return 1 for empty or null content to avoid treating it as multi-line.
        return (messageContent?.split('\n').length || 1);
    }

    // Add default initialization for message.expanded in existing messages
    initializeMessageExpansionState(): void {
        if (!this.project || !this.project.chatHistory) return; // Guard project and history

        // Ensure history array exists for the current sheet ID
        this.ensureChatHistoryExists(this.sheetid);

        this.project.chatHistory[this.sheetid].forEach((message: ChatMessage) => {
            if (message.expanded === undefined) {
                // Default to expanded if 5 lines or less, collapsed otherwise
                message.expanded = this.getMessageLineCount(message.content) <= 5;
            }
        });
        // Trigger change detection after potentially modifying the array elements
        this.cdr.detectChanges();
    }



    async addMultiViewScreenshots() {
        console.log('Adding multi-view screenshots (6 views)...');
        this.screenshotDropdown = false; // Close dropdown
        this.ensureSheetStateExists(this.sheetid);
        this.project.sheetStates[this.sheetid].isLoading = true;
        this.project.sheetStates[this.sheetid].loadingText = 'Capturing views...';
        this.cdr.detectChanges();

        try {
            const views = await this.captureMultiViews();
            // View information with camera positions and directions
            const viewInfo = [
                { name: 'Right', position: [50, 0, 0], direction: [-1, 0, 0] },
                { name: 'Left', position: [-50, 0, 0], direction: [1, 0, 0] },
                { name: 'Top', position: [0, 50, 0], direction: [0, -1, 0] }, // Common for Y-up scenes, Z-out of screen is 'up'
                { name: 'Bottom', position: [0, -50, 0], direction: [0, 1, 0] }, // Common for Y-up scenes, Z-into screen is 'up'
                { name: 'Front', position: [0, 0, 50], direction: [0, 0, -1] },
                { name: 'Back', position: [0, 0, -50], direction: [0, 0, 1] }
            ];

            views.forEach((viewData, index) => {
                if (!viewData) {
                    console.warn(`Skipping view ${viewInfo[index].name} due to capture error.`);
                    return; // Skip if viewData is null/undefined
                }

                const viewMetadata = viewInfo[index];
                const screenshot = {
                    type: 'image',
                    isScreenshot: true,
                    markingType: 'multi-view',
                    viewName: viewMetadata.name,
                    // Add camera information
                    cameraPosition: viewMetadata.position,
                    cameraDirection: viewMetadata.direction,
                    index: this.attachments.length,
                    data: viewData.dataUrl,
                    timestamp: new Date().toISOString()
                };
                this.attachments.push(screenshot);
                console.log(`${viewMetadata.name} view screenshot added.`);
            });
            this.cdr.detectChanges();
            this.toastr.success('All views captured from different camera positions.', 'Success');
        } catch (error) {
            console.error('Error capturing multi-view screenshots:', error);
            this.toastr.error('Failed to capture multi-view screenshots.', 'Error');
        } finally {
            this.ensureSheetStateExists(this.sheetid);
            this.project.sheetStates[this.sheetid].isLoading = false;
            this.project.sheetStates[this.sheetid].loadingText = '';
            this.cdr.detectChanges();
        }
    }

    private async captureMultiViews(): Promise<Array<{ dataUrl: string, width: number, height: number } | null>> {
        const views: Array<{ dataUrl: string, width: number, height: number } | null> = [];
        const originalState = this.storeCameraState(); // Stores state of this.camera

        // Define view configurations: direction is from scene center to camera position
        const viewsSetup = [
            { label: 'Right', direction: new THREE.Vector3(1, 0, 0), up: new THREE.Vector3(0, 1, 0) },
            { label: 'Left', direction: new THREE.Vector3(-1, 0, 0), up: new THREE.Vector3(0, 1, 0) },
            { label: 'Top', direction: new THREE.Vector3(0, 1, 0), up: new THREE.Vector3(0, 0, -1) }, // Common for Y-up scenes, Z-out of screen is 'up'
            { label: 'Bottom', direction: new THREE.Vector3(0, -1, 0), up: new THREE.Vector3(0, 0, 1) }, // Common for Y-up scenes, Z-into screen is 'up'
            { label: 'Front', direction: new THREE.Vector3(0, 0, 1), up: new THREE.Vector3(0, 1, 0) },
            { label: 'Back', direction: new THREE.Vector3(0, 0, -1), up: new THREE.Vector3(0, 1, 0) }
        ];

        const offscreenCanvas = document.createElement('canvas');
        offscreenCanvas.width = 640;
        offscreenCanvas.height = 640;

        const offscreenRenderer = new THREE.WebGLRenderer({
            canvas: offscreenCanvas,
            antialias: true,
            preserveDrawingBuffer: true // Essential for canvas.toDataURL() or toBlob()
        });
        offscreenRenderer.setSize(640, 640);
        // offscreenRenderer.setPixelRatio(window.devicePixelRatio); // Optional: for high-dpi rendering

        try {
            for (const viewSetup of viewsSetup) {
                const viewData = await this.captureView(viewSetup.direction, viewSetup.up, viewSetup.label, offscreenCanvas, offscreenRenderer);
                views.push(viewData);
            }
        } finally {
            // Dispose of the offscreen renderer
            if (offscreenRenderer) {
                offscreenRenderer.dispose();
            }
            // Restore original camera state for this.camera
            this.restoreCameraState(originalState);
            this.camera.updateProjectionMatrix(); // Ensure main camera is up-to-date
            this.camera.updateMatrixWorld(true); // Ensure matrices are current for the main renderer
            this.renderer.render(this.scene, this.camera); // Refresh main view
        }

        return views;
    }

    private async captureView(
        viewDirectionGlobal: THREE.Vector3, // Normalized direction from scene center to camera
        upVectorGlobal: THREE.Vector3,    // Global up vector for this view orientation
        label: string,
        offscreenCanvas: HTMLCanvasElement, // Passed in
        offscreenRenderer: THREE.WebGLRenderer // Passed in
    ): Promise<{ dataUrl: string, width: number, height: number } | null> {
        try {
            console.log(`[Capture View - ${label}] Starting ORTHOGRAPHIC capture (fitting to scene)...`);

            // Using OrthographicCamera now. Initial frustum values are placeholders.
            const viewCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 100000);
            viewCamera.zoom = 1; // Orthographic zoom is usually 1 unless specifically scaled.

            // 1. Calculate Scene Bounding Box (World Space)
            const sceneBoundingBox = new THREE.Box3();
            let visibleObjectFound = false;
            this.scene.traverseVisible((object: THREE.Object3D) => {
                if (object instanceof THREE.Mesh && object.geometry) { // Added check for object.geometry
                    // Ensure object's world matrix is up-to-date before getting the box
                    object.updateWorldMatrix(true, false);
                    const objectBox = new THREE.Box3().setFromObject(object, true); // true for precise from vertices
                    if (!objectBox.isEmpty()) {
                        sceneBoundingBox.union(objectBox);
                        visibleObjectFound = true;
                    } else {
                        console.warn(`[Capture View - ${label}] Mesh ${object.name || 'Unnamed'} has empty bounding box.`);
                    }
                }
            });

            console.log(`[Capture View - ${label}] Initial check: visibleObjectFound: ${visibleObjectFound}, sceneBoundingBox empty: ${sceneBoundingBox.isEmpty()}`);
            console.log(`[Capture View - ${label}] Raw Scene BBox: min (${sceneBoundingBox.min.x.toFixed(2)}, ${sceneBoundingBox.min.y.toFixed(2)}, ${sceneBoundingBox.min.z.toFixed(2)}), max (${sceneBoundingBox.max.x.toFixed(2)}, ${sceneBoundingBox.max.y.toFixed(2)}, ${sceneBoundingBox.max.z.toFixed(2)})`);

            if (!visibleObjectFound || sceneBoundingBox.isEmpty()) {
                console.error(`[Capture View - ${label}] CRITICAL: No visible meshes with valid bounding boxes found. Cannot calculate view. Returning null.`);
                return null;
            }

            const sceneCenter = new THREE.Vector3();
            sceneBoundingBox.getCenter(sceneCenter);
            const sceneSphere = new THREE.Sphere(); // Still useful for initial camera positioning distance
            sceneBoundingBox.getBoundingSphere(sceneSphere);
            sceneSphere.center.copy(sceneCenter); // Ensure sphere uses the precise center of the AABB

            console.log(`[Capture View - ${label}] Scene Center: (${sceneCenter.x.toFixed(2)}, ${sceneCenter.y.toFixed(2)}, ${sceneCenter.z.toFixed(2)})`);
            console.log(`[Capture View - ${label}] Scene Sphere Radius (for positioning ref): ${sceneSphere.radius.toFixed(2)}`);

            // 2. Position and Orient Orthographic Camera
            // Place camera far enough to see the whole scene; actual "zoom" is by frustum size.
            const initialCamDistance = Math.max(sceneSphere.radius * 3, 10); // Ensure it's reasonably far
            viewCamera.position.copy(sceneCenter).addScaledVector(viewDirectionGlobal, initialCamDistance);
            viewCamera.up.copy(upVectorGlobal);
            viewCamera.lookAt(sceneCenter);
            viewCamera.updateMatrixWorld(true); // IMPORTANT: Update world matrix before using its inverse

            console.log(`[Capture View - ${label}] Initial Camera Position: (${viewCamera.position.x.toFixed(2)}, ${viewCamera.position.y.toFixed(2)}, ${viewCamera.position.z.toFixed(2)})`);

            // 3. Transform Bounding Box to Camera's Local Space & Find Extents
            const cameraWorldInverse = viewCamera.matrixWorldInverse.clone();
            let minX_local = Infinity, maxX_local = -Infinity;
            let minY_local = Infinity, maxY_local = -Infinity;
            let minZ_local = Infinity, maxZ_local = -Infinity;

            const corners = [
                new THREE.Vector3(sceneBoundingBox.min.x, sceneBoundingBox.min.y, sceneBoundingBox.min.z),
                new THREE.Vector3(sceneBoundingBox.min.x, sceneBoundingBox.min.y, sceneBoundingBox.max.z),
                new THREE.Vector3(sceneBoundingBox.min.x, sceneBoundingBox.max.y, sceneBoundingBox.min.z),
                new THREE.Vector3(sceneBoundingBox.min.x, sceneBoundingBox.max.y, sceneBoundingBox.max.z),
                new THREE.Vector3(sceneBoundingBox.max.x, sceneBoundingBox.min.y, sceneBoundingBox.min.z),
                new THREE.Vector3(sceneBoundingBox.max.x, sceneBoundingBox.min.y, sceneBoundingBox.max.z),
                new THREE.Vector3(sceneBoundingBox.max.x, sceneBoundingBox.max.y, sceneBoundingBox.min.z),
                new THREE.Vector3(sceneBoundingBox.max.x, sceneBoundingBox.max.y, sceneBoundingBox.max.z),
            ];

            for (const corner of corners) {
                const localCorner = corner.clone().applyMatrix4(cameraWorldInverse);
                minX_local = Math.min(minX_local, localCorner.x);
                maxX_local = Math.max(maxX_local, localCorner.x);
                minY_local = Math.min(minY_local, localCorner.y);
                maxY_local = Math.max(maxY_local, localCorner.y);
                minZ_local = Math.min(minZ_local, localCorner.z);
                maxZ_local = Math.max(maxZ_local, localCorner.z);
            }
            console.log(`[Capture View - ${label}] Local Extents: X(${minX_local.toFixed(2)}, ${maxX_local.toFixed(2)}), Y(${minY_local.toFixed(2)}, ${maxY_local.toFixed(2)}), Z(${minZ_local.toFixed(2)}, ${maxZ_local.toFixed(2)})`);

            // 4. Calculate Orthographic Frustum Parameters
            const frustumWidth = (maxX_local - minX_local) * this.PADDING_FACTOR;
            const frustumHeight = (maxY_local - minY_local) * this.PADDING_FACTOR;
            const finalFrustumSize = Math.max(frustumWidth, frustumHeight, 0.1); // Ensure non-zero size

            const localCenterX = (minX_local + maxX_local) / 2;
            const localCenterY = (minY_local + maxY_local) / 2;

            viewCamera.left = localCenterX - finalFrustumSize / 2;
            viewCamera.right = localCenterX + finalFrustumSize / 2;
            viewCamera.top = localCenterY + finalFrustumSize / 2;
            viewCamera.bottom = localCenterY - finalFrustumSize / 2;

            // Camera looks down its -Z axis. Objects in front have negative Z in local space.
            // Near/Far are positive distances from the camera.
            const sceneDepthInView = maxZ_local - minZ_local;
            const zBuffer = Math.max(sceneDepthInView * 0.5, 1.0); // Generous buffer, at least 1 unit

            viewCamera.near = -maxZ_local - zBuffer; // maxZ_local is furthest point towards +Z local. If object is in front, maxZ_local is negative.
            viewCamera.far = -minZ_local + zBuffer;  // minZ_local is closest point towards +Z local.

            // Ensure near is positive and less than far, adjust if necessary
            if (viewCamera.near <= 0) viewCamera.near = 0.01;
            if (viewCamera.far <= viewCamera.near) viewCamera.far = viewCamera.near + Math.max(sceneDepthInView, 1.0) + 2 * zBuffer;

            console.log(`[Capture View - ${label}] Ortho Frustum: L:${viewCamera.left.toFixed(2)}, R:${viewCamera.right.toFixed(2)}, T:${viewCamera.top.toFixed(2)}, B:${viewCamera.bottom.toFixed(2)}, N:${viewCamera.near.toFixed(2)}, F:${viewCamera.far.toFixed(2)}`);

            // 5. Update Projection Matrix
            viewCamera.updateProjectionMatrix();

            console.log(`[Capture View - ${label}] Camera Details: Zoom=${viewCamera.zoom}`);

            // 6. Render the scene
            offscreenRenderer.setViewport(0, 0, offscreenCanvas.width, offscreenCanvas.height);
            offscreenRenderer.render(this.scene, viewCamera);
            await new Promise(resolve => setTimeout(resolve, 50)); // Reduced delay
            offscreenRenderer.render(this.scene, viewCamera);
            await new Promise(resolve => setTimeout(resolve, 50)); // Reduced delay

            const result = await this.captureWebGLCanvas(offscreenCanvas, label, viewCamera);
            return result;

        } catch (captureError) {
            console.error(`Failed to capture view: ${label}`, captureError);
            this.toastr.warning(`Failed to capture ${label} view`, 'Capture Warning', { timeOut: 3000 });
            return null;
        }
    }

    // This function captures the WebGL canvas and draws the overlay
    private async captureWebGLCanvas(canvas: HTMLCanvasElement, viewLabel: string, camera: THREE.Camera): Promise<{ dataUrl: string, width: number, height: number }> {
        return new Promise((resolve, reject) => {
            try {
                // Create a temporary 2D canvas
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = canvas.width;
                tempCanvas.height = canvas.height;
                const ctx = tempCanvas.getContext('2d');

                if (!ctx) {
                    return reject(new Error('Failed to get 2D context for overlay canvas'));
                }

                // 1. Draw the current WebGL canvas content (the rendered 3D view)
                ctx.drawImage(canvas, 0, 0);

                // 2. Draw the axes overlay on top
                // *** This is where the axes are drawn ***
                // It needs the current camera to project 3D axes to 2D
                // Add logging here to check camera state for each view
                console.log(`[captureWebGLCanvas - ${viewLabel}] Drawing axes. Camera Pos: ${camera.position.toArray().map(v => v.toFixed(1)).join(', ')}, Up: ${camera.up.toArray().map(v => v.toFixed(1)).join(', ')}`);

                // *** BEGIN 3D AXIS PROJECTION CODE ***
                const axisLength = 15; // Length of axes in 3D world units (adjust as needed)
                const origin = new THREE.Vector3(0, 0, 0);
                const xAxisEnd = new THREE.Vector3(axisLength, 0, 0);
                const yAxisEnd = new THREE.Vector3(0, axisLength, 0);
                const zAxisEnd = new THREE.Vector3(0, 0, axisLength);

                ctx.lineWidth = 2;
                ctx.font = 'bold 12px Arial';

                // Function to project 3D point to 2D canvas coordinates
                const projectTo2D = (point3D: THREE.Vector3): THREE.Vector2 | null => {
                    // Clone the point before projecting to avoid modifying the original
                    const projected = point3D.clone().project(camera); // Use the passed-in camera

                    // Check if the point is behind the camera (z > 1 in NDC)
                    // or outside the normalized clip space [-1, 1]
                    if (projected.z > 1 || Math.abs(projected.x) > 1 || Math.abs(projected.y) > 1) {
                        return null; // Don't draw if point is behind or outside view
                    }

                    // Convert Normalized Device Coordinates (NDC) [-1, 1] to canvas coordinates [0, width/height]
                    const x = (projected.x + 1) * canvas.width / 2;
                    // NDC y is usually [-1, 1] with +1 at top, canvas y is [0, height] with 0 at top
                    const y = (-projected.y + 1) * canvas.height / 2;

                    return new THREE.Vector2(x, y);
                };

                const origin2D = projectTo2D(origin);

                if (origin2D) {
                    // Draw X axis (Red)
                    const xAxisEnd2D = projectTo2D(xAxisEnd);
                    if (xAxisEnd2D) {
                        ctx.strokeStyle = 'red';
                        ctx.fillStyle = 'red';
                        ctx.beginPath();
                        ctx.moveTo(origin2D.x, origin2D.y);
                        ctx.lineTo(xAxisEnd2D.x, xAxisEnd2D.y);
                        ctx.stroke();
                        ctx.fillText('X', xAxisEnd2D.x + 5, xAxisEnd2D.y + 5);
                    }

                    // Draw Y axis (Green - Lime)
                    const yAxisEnd2D = projectTo2D(yAxisEnd);
                    if (yAxisEnd2D) {
                        ctx.strokeStyle = 'lime';
                        ctx.fillStyle = 'lime';
                        ctx.beginPath();
                        ctx.moveTo(origin2D.x, origin2D.y);
                        ctx.lineTo(yAxisEnd2D.x, yAxisEnd2D.y);
                        ctx.stroke();
                        ctx.fillText('Y', yAxisEnd2D.x + 5, yAxisEnd2D.y - 5); // Adjust label position slightly
                    }

                    // Draw Z axis (Blue)
                    const zAxisEnd2D = projectTo2D(zAxisEnd);
                    if (zAxisEnd2D) {
                        ctx.strokeStyle = 'blue';
                        ctx.fillStyle = 'blue';
                        ctx.beginPath();
                        ctx.moveTo(origin2D.x, origin2D.y);
                        ctx.lineTo(zAxisEnd2D.x, zAxisEnd2D.y);
                        ctx.stroke();
                        ctx.fillText('Z', zAxisEnd2D.x - 5, zAxisEnd2D.y + 5); // Adjust label position slightly
                    }
                } else {
                    // Origin itself might be outside the view in some extreme cases
                    console.warn(`[captureWebGLCanvas - ${viewLabel}] Origin (0,0,0) could not be projected onto the canvas.`);
                }
                // *** END 3D AXIS PROJECTION CODE ***

                // 2b. Draw the dynamic 2D axes overview (top-right corner) - REVISED
                console.log(`[captureWebGLCanvas - ${viewLabel}] Drawing dynamic 2D axis overview...`);
                // *** BEGIN REVISED DYNAMIC 2D AXIS OVERVIEW CODE (TOP-RIGHT) ***
                { // Use a block scope
                    const overviewAxisSize = Math.min(canvas.width, canvas.height) * 0.06; // Slightly smaller gizmo size
                    const overviewMargin = 30; // More margin
                    // Define the fixed 2D center point for the gizmo in the top-right
                    const overviewCenterX = canvas.width - overviewMargin;
                    const overviewCenterY = overviewMargin;

                    ctx.lineWidth = 2;
                    ctx.font = 'bold 10px Arial';
                    ctx.textAlign = 'center'; // Center text for better placement
                    ctx.textBaseline = 'middle'; // Center text vertically

                    // Project the world origin (0,0,0) and the endpoints of unit vectors (1,0,0), (0,1,0), (0,0,1)
                    const originPoint = new THREE.Vector3(0, 0, 0);
                    const xAxisEndPoint = new THREE.Vector3(1, 0, 0); // Using unit vectors for direction
                    const yAxisEndPoint = new THREE.Vector3(0, 1, 0);
                    const zAxisEndPoint = new THREE.Vector3(0, 0, 1);

                    const origin2D_overview = projectTo2D(originPoint); // Use projectTo2D with the passed camera
                    const xAxisEnd2D_overview = projectTo2D(xAxisEndPoint);
                    const yAxisEnd2D_overview = projectTo2D(yAxisEndPoint);
                    const zAxisEnd2D_overview = projectTo2D(zAxisEndPoint);

                    // Draw only if the origin and all endpoints project successfully
                    if (origin2D_overview && xAxisEnd2D_overview && yAxisEnd2D_overview && zAxisEnd2D_overview) {
                        // Calculate the normalized 2D direction vector from projected origin to projected endpoint
                        const dirX = xAxisEnd2D_overview.clone().sub(origin2D_overview).normalize();
                        const dirY = yAxisEnd2D_overview.clone().sub(origin2D_overview).normalize();
                        const dirZ = zAxisEnd2D_overview.clone().sub(origin2D_overview).normalize();

                        const labelOffset = 10; // How far beyond the line end to place the label

                        // Draw X axis (Red) from the fixed overview center
                        ctx.strokeStyle = 'red';
                        ctx.fillStyle = 'red';
                        ctx.beginPath();
                        ctx.moveTo(overviewCenterX, overviewCenterY);
                        const endX_x = overviewCenterX + dirX.x * overviewAxisSize;
                        const endX_y = overviewCenterY + dirX.y * overviewAxisSize;
                        ctx.lineTo(endX_x, endX_y);
                        ctx.stroke();
                        // Place label slightly further out along the projected direction
                        ctx.fillText('X', overviewCenterX + dirX.x * (overviewAxisSize + labelOffset),
                            overviewCenterY + dirX.y * (overviewAxisSize + labelOffset));

                        // Draw Y axis (Green) from the fixed overview center
                        ctx.strokeStyle = 'lime';
                        ctx.fillStyle = 'lime';
                        ctx.beginPath();
                        ctx.moveTo(overviewCenterX, overviewCenterY);
                        const endY_x = overviewCenterX + dirY.x * overviewAxisSize;
                        const endY_y = overviewCenterY + dirY.y * overviewAxisSize;
                        ctx.lineTo(endY_x, endY_y);
                        ctx.stroke();
                        ctx.fillText('Y', overviewCenterX + dirY.x * (overviewAxisSize + labelOffset),
                            overviewCenterY + dirY.y * (overviewAxisSize + labelOffset));

                        // Draw Z axis (Blue) from the fixed overview center
                        ctx.strokeStyle = 'blue';
                        ctx.fillStyle = 'blue';
                        ctx.beginPath();
                        ctx.moveTo(overviewCenterX, overviewCenterY);
                        const endZ_x = overviewCenterX + dirZ.x * overviewAxisSize;
                        const endZ_y = overviewCenterY + dirZ.y * overviewAxisSize;
                        ctx.lineTo(endZ_x, endZ_y);
                        ctx.stroke();
                        ctx.fillText('Z', overviewCenterX + dirZ.x * (overviewAxisSize + labelOffset),
                            overviewCenterY + dirZ.y * (overviewAxisSize + labelOffset));

                    } else {
                        console.warn(`[captureWebGLCanvas - ${viewLabel}] Could not project axes for top-right overview.`);
                        // Optional: Draw a fallback static indicator if projection fails?
                        // For now, just logs a warning.
                    }
                } // End block scope for top-right overview
                // *** END REVISED DYNAMIC 2D AXIS OVERVIEW CODE ***

                // 3. Draw the view label
                ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
                ctx.fillRect(5, 5, 80, 20); // Background for label
                ctx.font = '12px Arial';
                ctx.fillStyle = 'white';
                ctx.fillText(viewLabel, 10, 18);

                // 4. Get the final image data URL
                const dataUrl = tempCanvas.toDataURL('image/png');
                resolve({ dataUrl, width: canvas.width, height: canvas.height });

            } catch (error) {
                console.error('Error capturing WebGL canvas with overlay:', error);
                reject(error);
            }
        });
    }

    // Helper methods to store and restore camera state (simplified from report-creator)
    private storeCameraState() {
        return {
            position: this.camera.position.clone(),
            rotation: this.camera.rotation.clone(),
            quaternion: this.camera.quaternion.clone(),
            up: this.camera.up.clone(),
            zoom: this.camera.zoom
        };
    }

    private restoreCameraState(state: any) {
        this.camera.position.copy(state.position);
        this.camera.rotation.copy(state.rotation);
        this.camera.quaternion.copy(state.quaternion);
        this.camera.up.copy(state.up);
        this.camera.zoom = state.zoom;
        this.camera.updateProjectionMatrix();
        this.camera.updateMatrixWorld();
    }

    // Add the ensureChatHistoryExists method after ensureSheetStateExists
    private ensureChatHistoryExists(sheetId: number): void {
        if (!this.project) {
            console.warn(`AI Editor: ensureChatHistoryExists called for sheet ${sheetId} before project input was received.`);
            return;
        }

        // Initialize chatHistory in project if it doesn't exist
        if (!this.project.chatHistory) {
            this.project.chatHistory = {};
        }

        if (!this.project.chatHistory[sheetId]) {
            this.project.chatHistory[sheetId] = [];
        }
    }

    // Add abortCheckRequest and abortAnalyzeRequest methods after abortCurrentRequest method

    // Add the handlePrimaryAction method
    handlePrimaryAction(): void {
        this.ensureSheetStateExists(this.sheetid);
        if (this.project?.sheetStates?.[this.sheetid]?.isSubmitting) {
            console.log('Stop button clicked, calling abortSubmit...');
            this.abortSubmit();
        } else {
            console.log('Submit button clicked, calling onAISubmit...');
            this.onAISubmit();
        }
    }

    // Check if any sheet is currently submitting
    isAnySheetSubmitting(): boolean {
        if (!this.project?.sheetStates) return false;
        return Object.values(this.project.sheetStates).some((state: any) => state.isSubmitting);
    }

    // Submit the same query to all sheets in parallel
    async submit5x(model?: string): Promise<void> {
        if (model) {
            this.selectedModel = model;
        }

        if (!this.project?.sheets || !this.aiPrompt?.trim()) {
            console.warn('No sheets available or empty prompt for 5x submission');
            return;
        }

        const originalPrompt = this.aiPrompt;
        const originalSheetId = this.sheetid;
        const originalAttachments = [...this.attachments]; // Copy attachments

        console.log(`Starting 5x parallel submission with prompt: "${originalPrompt}" across ${this.project.sheets.length} sheets`);

        try {
            // Create array of submission promises for parallel execution
            const submissionPromises = [];

            for (let i = 0; i < this.project.sheets.length; i++) {
                console.log(`5x submission: Preparing sheet ${i + 1}/${this.project.sheets.length}`);

                // Ensure state exists for this sheet
                this.ensureSheetStateExists(i);
                this.ensureChatHistoryExists(i);

                // Create a submission promise for this sheet
                const submissionPromise = this.submitToSheet(i, originalPrompt, originalAttachments);
                submissionPromises.push(submissionPromise);
            }

            // Execute all submissions in parallel
            console.log(`Starting ${submissionPromises.length} parallel submissions...`);
            await Promise.all(submissionPromises);
            console.log('All 5x submissions completed successfully');

        } catch (error) {
            console.error('Error during 5x submission:', error);
            this.toastr.error('Error occurred during 5x submission', 'Error');
        } finally {
            // Restore original sheet and clear prompt
            this.sheetid = originalSheetId;
            this.aiPrompt = '';
            this.attachments = [];
            this.aiPromptChangee.emit('');
            console.log('5x submission completed, restored to original sheet');
        }
    }

    // Helper method to submit to a specific sheet
    private async submitToSheet(sheetId: number, prompt: string, attachments: any[]): Promise<void> {
        const requestSheetId = sheetId;
        console.log(`AI request initiated for sheetId: ${requestSheetId}`);

        // Ensure state exists for the request sheet
        this.ensureSheetStateExists(requestSheetId);

        try {
            // Update state directly
            this.project.sheetStates[requestSheetId].isSubmitting = true;
            this.project.sheetStates[requestSheetId].isLoading = true;
            this.project.sheetStates[requestSheetId].isProcessingResponse = true;

            // Set sheet state to running
            if (this.project.sheets && this.project.sheets[requestSheetId]) {
                this.project.sheets[requestSheetId].state = " [running]";
            }

            this.cdr.detectChanges(); // Trigger update

            this.ensureChatHistoryExists(requestSheetId);

            // Initialize expanded state when adding user message
            this.project.chatHistory[requestSheetId].push({
                role: 'user',
                content: prompt,
                expanded: this.getMessageLineCount(prompt) <= 5
            });

            this.chatHistoryChange.emit({ [requestSheetId]: this.project.chatHistory[requestSheetId] });

            const abortController = new AbortController();
            const signal = abortController.signal;

            console.log(`Using AI Provider: ${this.aiProvider} for sheet ${requestSheetId}`);
            this.project.sheetStates[requestSheetId].loadingText = `Processing...`;
            this.cdr.detectChanges(); // Update text

            // Prepare API call data
            let table = attachments.find(a => a.type === 'values' || a.type === 'formulas');
            let base64images = attachments.filter(a => a.type === 'image');
            let base64pdfs = attachments
                .filter(a => a.type === 'document' && a.fileType === 'pdf')
                .map(a => ({ data: a.data, fileName: a.fileName }));

            // Remove empty rows from table if it exists
            if (table) {
                let values = table.formulas;
                if (Array.isArray(values)) {
                    let lastNonEmptyRow = values.length - 1;
                    while (lastNonEmptyRow >= 0) {
                        const row = values[lastNonEmptyRow];
                        if (row.some(cell => cell !== "")) {
                            break;
                        }
                        lastNonEmptyRow--;
                    }
                    if (lastNonEmptyRow >= 0) {
                        table.formulas = values.slice(0, lastNonEmptyRow + 1);
                    }
                }
            }

            let systemPrompt;
            switch (this.aiProvider) {
                case 'claude': systemPrompt = SystemPrompt.claudePrompt; break;
                case 'gpt-4o': systemPrompt = SystemPrompt.openaiPrompt; break;
                case 'gpt-4.1': systemPrompt = SystemPrompt.openaiPrompt; break;
                case 'o3': systemPrompt = SystemPrompt.openaiPrompt; break;
                case 'gemini-2.5-flash': systemPrompt = SystemPrompt.geminiPrompt; break;
                case 'gemini-2.5-pro': systemPrompt = SystemPrompt.geminiPrompt; break;
                case 'deepseek': systemPrompt = SystemPrompt.deepseekPrompt; break;
                default: systemPrompt = SystemPrompt.claudePrompt;
            }

            if (table) {
                if (table.excelRange && table.excelRange.trim() !== "") {
                    systemPrompt += ` You are editing a table. You should focus on the range '${table.excelRange}'. The data for this specific range is: "${JSON.stringify(table.formulas)}". Please provide the edited data specifically for this range.`;
                }
            }
            systemPrompt += ` The following table data is provided for editing: "${JSON.stringify(this.sheetformulas)}". Please provide the edited version of this data.`;

            if (base64images?.length > 0) {
                systemPrompt += ` You have ${base64images.length} image(s) of the model of the current situation.`;
            }

            const requestSheetChatHistory = this.project?.chatHistory?.[requestSheetId] || [];
            if (requestSheetChatHistory.length > 0) {
                systemPrompt += "\n\nHere is the conversation history so far:\n";
                requestSheetChatHistory.forEach(msg => {
                    systemPrompt += `\n${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}\n`;
                });
                systemPrompt += "\nPlease continue the conversation based on this history.";
            }

            let endpoint = '';
            let payload: any = {
                userInput: prompt,
                context: '',
                systemPrompt,
                base64images,
                table: JSON.stringify(table?.formulas),
                temperature: this.temperature,
                base64pdfs
            };

            switch (this.aiProvider) {
                case 'claude': endpoint = '/createClaudePrompt'; break;
                case 'gpt-4o':
                case 'gpt-4.1':
                case 'o4-mini':
                case 'o3':
                    endpoint = '/createOpenAIPrompt';
                    payload.modelName = this.aiProvider;
                    break;
                case 'gemini-2.5-flash':
                case 'gemini-2.5-flash-lite-preview-06-17':
                case 'gemini-2.5-pro':
                    endpoint = '/createGeminiPrompt';
                    payload.modelName = this.aiProvider;
                    payload.thinkingEnabled = this.geminiThinkingEnabled; // Add thinking mode preference
                    break;
                case 'deepseek': endpoint = '/createDeepSeekPrompt'; break;
                default:
                    console.error("Unsupported AI provider:", this.aiProvider);
                    throw new Error(`Unsupported AI provider selected: ${this.aiProvider}`);
            }

            console.log(`Making API request to: ${endpoint} for sheet ${requestSheetId}`);

            const response = await fetch(this.apiUrl + endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload),
                signal
            });

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch (jsonError) {
                    throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
                }
                let errorMessage = 'API request failed';
                if (errorData.details) {
                    errorMessage = errorData.details;
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                }
                console.log("Error data:", errorData);
                throw new Error(errorMessage);
            }

            const data = await response.json();
            if (data.error) {
                let errorMessage = data.details || data.error;
                if (data.errorOrigin) {
                    errorMessage = `API error: ${errorMessage}`;
                }
                throw new Error(errorMessage);
            }

            let rawResponseContent = data.response;

            // Strip JSON markdown fences for certain providers
            if (typeof rawResponseContent === 'string' && (this.aiProvider.startsWith('gemini-') || this.aiProvider.startsWith('o3'))) {
                let trimmedContent = rawResponseContent.trim();
                if (trimmedContent.startsWith('```json\n') && trimmedContent.endsWith('\n```')) {
                    console.log(`[Sheet ${requestSheetId}] Found JSON markers. Stripping...`);
                    trimmedContent = trimmedContent.substring('```json\n'.length, trimmedContent.length - '\n```'.length).trim();
                    rawResponseContent = trimmedContent;
                } else if (trimmedContent.startsWith('```') && trimmedContent.endsWith('```')) {
                    console.log(`[Sheet ${requestSheetId}] Found generic code fence markers. Stripping...`);
                    trimmedContent = trimmedContent.substring('```'.length, trimmedContent.length - '```'.length).trim();
                    if (trimmedContent.startsWith('{') || trimmedContent.startsWith('[')) {
                        rawResponseContent = trimmedContent;
                    }
                }
            }

            // Process response and emit events
            if (typeof rawResponseContent === 'string') {
                const trimmedResponse = rawResponseContent.trim();
                if ((trimmedResponse.startsWith('[') && trimmedResponse.endsWith(']')) ||
                    (trimmedResponse.startsWith('{') && trimmedResponse.endsWith('}'))) {
                    try {
                        let parsedResponse = JSON.parse(trimmedResponse);
                        if (Array.isArray(parsedResponse)) {
                            for (let i = 0; i < parsedResponse.length; i++) {
                                if (!Array.isArray(parsedResponse[i])) {
                                    parsedResponse[i] = [parsedResponse[i]];
                                }
                            }
                        }

                        this.copyTableData.emit({ data: parsedResponse, run: null, sheetId: requestSheetId });
                        this.project.chatHistory[requestSheetId].push({
                            role: 'assistant',
                            content: rawResponseContent,
                            expanded: this.getMessageLineCount(rawResponseContent) <= 5
                        });
                    } catch (parseError) {
                        console.error(`[Sheet ${requestSheetId}] JSON parse error:`, parseError);
                        this.project.chatHistory[requestSheetId].push({
                            role: 'assistant',
                            content: rawResponseContent,
                            expanded: this.getMessageLineCount(rawResponseContent) <= 5
                        });
                    }
                } else {
                    this.project.chatHistory[requestSheetId].push({
                        role: 'assistant',
                        content: rawResponseContent,
                        expanded: this.getMessageLineCount(rawResponseContent) <= 5
                    });
                }
            }

            this.chatHistoryChange.emit({ [requestSheetId]: this.project.chatHistory[requestSheetId] });

        } catch (error) {
            console.error(`Error in sheet ${requestSheetId} submission:`, error);
            this.ensureChatHistoryExists(requestSheetId);
            this.project.chatHistory[requestSheetId].push({
                role: 'assistant',
                content: `Error: ${error.message}`,
                expanded: true
            });
            this.chatHistoryChange.emit({ [requestSheetId]: this.project.chatHistory[requestSheetId] });
            throw error; // Re-throw to be caught by Promise.all
        } finally {
            // Reset state
            this.project.sheetStates[requestSheetId].isSubmitting = false;
            this.project.sheetStates[requestSheetId].isLoading = false;
            this.project.sheetStates[requestSheetId].isProcessingResponse = false;

            // Set sheet state to done
            if (this.project.sheets && this.project.sheets[requestSheetId]) {
                this.project.sheets[requestSheetId].state = " [done]";
            }
            this.cdr.detectChanges();
        }
    }

    openDrawingCanvas(): void {
        const dialogRef = this.dialog.open(DrawingCanvasComponent, {
            width: '90vw',       // Use 90% of viewport width
            maxWidth: '95vw',    // Maximum width constraint
            height: '90vh',      // Set height to 90% of viewport height
            maxHeight: '95vh',   // Maximum height constraint
            panelClass: 'drawing-dialog-container', // Custom class for styling container
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                // result contains the base64 data URL from the canvas
                console.log('Drawing Canvas closed, result:', result.substring(0, 30) + '...');
                this.attachments.push({
                    type: 'image', // Treat drawing as an image attachment
                    data: result,
                    fileName: 'drawing.png', // Assign a default filename
                    isDrawing: true // Add a flag to identify it as a drawing if needed
                });
                this.cdr.detectChanges(); // Update the UI to show the new attachment
            }
        });
    }

    generateSubProjects() {
        if (!this.aiPrompt?.trim()) return;

        const currentSheetId = this.sheetid;
        if (this.project && this.project.sheetStates && this.project.sheetStates[currentSheetId]) {
            this.project.sheetStates[currentSheetId].isGeneratingSubProjects = true;
            this.project.sheetStates[currentSheetId].loadingText = 'Generating sub-projects...'; // Optional: Set loading text
        }

        this.aiAgentService.generateSubPrompts(this.aiPrompt, this.aiProvider)
            .pipe(
                finalize(() => {
                    if (this.project && this.project.sheetStates && this.project.sheetStates[currentSheetId]) {
                        this.project.sheetStates[currentSheetId].isGeneratingSubProjects = false;
                        this.project.sheetStates[currentSheetId].loadingText = ''; // Clear loading text
                    }
                })
            )
            .subscribe({
                next: (response) => {
                    console.log('Sub-projects generated:', response);
                    // TODO: Implement logic to handle the response
                    // e.g., display them in a dialog, create new sheets, etc.
                    this.toastr.success('Sub-projects generated successfully! Check the console.');

                    // Example: Add response to chat history (optional)
                    if (this.project && this.project.chatHistory && this.project.chatHistory[currentSheetId]) {
                        this.project.chatHistory[currentSheetId].push({
                            role: 'assistant',
                            content: `Generated Sub-Projects:\n${JSON.stringify(response, null, 2)}`,
                            timestamp: new Date().toISOString(),
                            expanded: true
                        });
                        this.scrollChatToBottom();
                    }
                },
                error: (error) => {
                    console.error('Error generating sub-projects:', error);
                    this.toastr.error(`Failed to generate sub-projects: ${error.message}`);
                    // Also add error to chat history
                    if (this.project && this.project.chatHistory && this.project.chatHistory[currentSheetId]) {
                        this.project.chatHistory[currentSheetId].push({
                            role: 'assistant',
                            content: `Error generating sub-projects: ${error.message}`,
                            timestamp: new Date().toISOString(),
                            expanded: true
                        });
                        this.scrollChatToBottom(); // Corrected function name
                    }
                }
            });
    }

    generateGeminiStream(): void {
        if (!this.userInput.trim() && this.base64images.length === 0 && this.base64pdfs.length === 0) {
            this.toastr.warning('Please enter a prompt or upload an image/PDF.', 'Input Required');
            return;
        }
        this.isLoading = true;
        this.aiResponse = ''; // Clear previous non-streamed response
        this.streamedAiContent = ''; // Clear previous streamed response
        this.isStreamingResponse = true;

        const payload = {
            userInput: this.userInput,
            context: '', // Add context if needed, e.g., this.chatHistory[this.chatHistory.length -1 ]?.ai
            base64images: this.base64images,
            base64pdfs: this.base64pdfs,
            table: this.includeSpreadsheetData ? this.getFormattedSpreadsheetData() : null,
            systemPrompt: this.currentSystemPrompt,
            modelName: this.selectedModel,
            thinkingEnabled: this.geminiThinkingEnabled // Add thinking mode preference for streaming
        };

        this.aiAgentService.generateGeminiStream(payload).subscribe({
            next: (chunk: string) => {
                this.streamedAiContent += chunk;
                // Infrequent CDR, or rely on Angular's default for text updates.
                // If rendering is laggy, can call this.cdr.detectChanges() here but be mindful of performance.
            },
            error: (error: any) => {
                console.error('Error streaming Gemini response:', error);
                this.toastr.error(error.message || 'Failed to get streamed response from Gemini', 'Streaming Error');
                this.isStreamingResponse = false;
                this.isLoading = false;
                this.aiResponse = 'Error: ' + (error.message || 'Failed to stream data.');
                this.cdr.detectChanges();
            },
            complete: () => {
                this.isStreamingResponse = false;
                this.isLoading = false;
                this.aiResponse = this.streamedAiContent; // Final content
                this.toastr.success('Gemini stream completed', 'Stream Complete');
                this.saveChat();
                this.cdr.detectChanges();
            }
        });
    }

    generateText(): void {
        if (!this.userInput.trim() && this.base64images.length === 0 && this.base64pdfs.length === 0) {
            this.toastr.warning('Please enter a prompt or upload an image/PDF.', 'Input Required');
            return;
        }

        this.isLoading = true;
        this.aiResponse = '';
        this.streamedAiContent = ''; // Always clear streamed content when starting a new generation
        this.isStreamingResponse = false; // Reset streaming state

        const payload = {
            userInput: this.userInput,
            context: this.getChatContext(),
            base64images: this.base64images,
            base64pdfs: this.base64pdfs,
            table: this.includeSpreadsheetData ? this.getFormattedSpreadsheetData() : null,
            systemPrompt: this.currentSystemPrompt,
            modelName: this.selectedModel,
            thinkingEnabled: this.geminiThinkingEnabled, // Add thinking mode preference
            // provider: this.selectedProvider // Provider is handled by the calling method in service
        };

        let apiCall: Observable<any>;

        switch (this.selectedProvider) {
            case 'openai':
                apiCall = this.aiAgentService.generateOpenAIText(payload);
                break;
            case 'claude':
                apiCall = this.aiAgentService.generateClaudeText(payload);
                break;
            case 'gemini':
                if (this.useStreamingForGemini) {
                    this.generateGeminiStream(); // This sets its own isLoading, etc.
                    return; // Exit generateText as generateGeminiStream handles the full lifecycle
                } else {
                    apiCall = this.aiAgentService.generateGeminiText(payload);
                }
                break;
            case 'deepseek':
                apiCall = this.aiAgentService.generateDeepSeekText(payload);
                break;
            case 'grok':
                apiCall = this.aiAgentService.generateGrokText(payload);
                break;
            default:
                this.toastr.error('Invalid AI provider selected.', 'Error');
                this.isLoading = false;
                return;
        }

        apiCall.subscribe({
            next: (res: any) => {
                this.isLoading = false;
                this.aiResponse = res.response; // For non-streaming
                this.generatedSubProjects = res.subProjects || [];
                this.cdr.detectChanges();
                this.saveChat();
                if (this.generatedSubProjects.length > 0) {
                    this.askToExecuteSubProjects();
                }
            },
            error: (error: any) => {
                this.isLoading = false;
                this.toastr.error(error.message || 'Failed to get response from AI', 'Error');
                this.aiResponse = 'Error: ' + (error.message || 'Could not fetch data.');
                this.cdr.detectChanges();
            }
        });
    }

    // ... existing code ...
    // Make sure no duplicate `useStreamingForGemini` is defined if it was added by a partial edit

    @ViewChild('chatMessagesContainer') private chatMessagesContainer!: ElementRef;
    @ViewChild('fileInput') fileInput!: ElementRef;
    private editor: any;
    // useStreamingForGemini: boolean = true; // Already added at the top

    // Stub for missing method
    getFormattedSpreadsheetData(): any {
        console.warn('getFormattedSpreadsheetData() is not fully implemented.');
        if (this.includeSpreadsheetData && this.activeSheetData.length > 0) {
            // Basic implementation: return activeSheetData or a stringified version
            // A more robust implementation would format it as expected by the AI.
            return JSON.stringify(this.activeSheetData);
        }
        return null;
    }

    // Stub for missing method
    saveChat(): void {
        console.warn('saveChat() is not fully implemented.');
        // Implementation would likely involve:
        // 1. Getting current chat history (e.g., this.project.chatHistory[this.sheetid])
        // 2. Saving it to local storage, a service, or backend.
        // Example: localStorage.setItem(`chatHistory_${this.sheetid}`, JSON.stringify(this.project.chatHistory[this.sheetid]));
        this.chatHistoryChange.emit({ [this.sheetid]: this.project.chatHistory[this.sheetid] }); // Notify parent
    }

    // Stub for missing method
    getChatContext(): string {
        console.warn('getChatContext() is not fully implemented.');
        // Implementation would typically concatenate recent messages from chat history
        // Example: return this.project.chatHistory[this.sheetid]?.slice(-3).map(m => `${m.role}: ${m.content}`).join('\n') || '';
        const currentSheetHistory = this.project?.chatHistory?.[this.sheetid];
        if (currentSheetHistory && currentSheetHistory.length > 1) {
            // Return the last assistant message as context, if available
            const lastMessage = currentSheetHistory[currentSheetHistory.length - 1];
            if (lastMessage.role === 'assistant') {
                return lastMessage.content;
            }
            // Or the one before last if the last one was the user's prompt
            if (currentSheetHistory.length > 1) {
                const secondLastMessage = currentSheetHistory[currentSheetHistory.length - 2];
                if (secondLastMessage.role === 'assistant') {
                    return secondLastMessage.content;
                }
            }
        }
        return '';
    }

    // Stub for missing method
    askToExecuteSubProjects(): void {
        console.warn('askToExecuteSubProjects() is not fully implemented.');
        // Implementation would typically show a dialog to the user asking if they want to process the generated sub-projects.
        // If confirmed, it might loop through this.generatedSubProjects and create new sheets/tabs or initiate new AI calls.
        this.toastr.info('Sub-projects generated. Execution step is not yet implemented.', 'Info');
    }

    selectExamplePrompt(prompt: string): void {
        this.aiPrompt = prompt;
        this.cdr.detectChanges();

        // Auto-resize textarea after setting prompt
        setTimeout(() => {
            const textarea = document.getElementById('context') as HTMLTextAreaElement;
            if (textarea) {
                this.adjustTextareaHeight({ target: textarea });
            }
        }, 0);
    }

    // Prompt library menu methods
    togglePromptLibraryMenu(): void {
        this.showPromptLibraryMenu = !this.showPromptLibraryMenu;
        if (!this.showPromptLibraryMenu) {
            this.selectedCategory = null;
            this.selectedSubcategory = null;
        } else {
            // Position the dropdown dynamically
            setTimeout(() => {
                this.positionDropdown();
            }, 0);
        }
        this.cdr.detectChanges();
    }

    private positionDropdown(): void {
        const button = document.querySelector('.prompt-library-menu-button') as HTMLElement;
        const dropdown = document.querySelector('.prompt-library-dropdown') as HTMLElement;

        if (button && dropdown) {
            const buttonRect = button.getBoundingClientRect();
            const dropdownWidth = 500; // min-width from CSS
            const dropdownHeight = 400; // max-height from CSS

            // Calculate position to the right of the button
            let left = buttonRect.right + 10; // 10px gap from button
            let top = buttonRect.top;

            // Check if dropdown would go off the right edge of the screen
            if (left + dropdownWidth > window.innerWidth) {
                // Position to the left of the button instead
                left = buttonRect.left - dropdownWidth - 10;
            }

            // Check if dropdown would go off the bottom of the screen
            if (top + dropdownHeight > window.innerHeight) {
                top = window.innerHeight - dropdownHeight - 20; // 20px margin from bottom
            }

            // Make sure it doesn't go above the top of the screen
            if (top < 20) {
                top = 20; // 20px margin from top
            }

            dropdown.style.left = `${left}px`;
            dropdown.style.top = `${top}px`;
        }
    }

    selectCategory(category: PromptCategory): void {
        this.selectedCategory = category;
        this.selectedSubcategory = null;
        this.cdr.detectChanges();
    }

    selectSubcategory(subcategory: PromptCategory): void {
        this.selectedSubcategory = subcategory;
        this.cdr.detectChanges();
    }

    selectLibraryPrompt(promptExample: PromptExample): void {
        this.aiPrompt = promptExample.prompt;
        this.showPromptLibraryMenu = false;
        this.selectedCategory = null;
        this.selectedSubcategory = null;
        this.cdr.detectChanges();

        // Auto-resize textarea after setting prompt
        setTimeout(() => {
            const textarea = document.getElementById('context') as HTMLTextAreaElement;
            if (textarea) {
                this.adjustTextareaHeight({ target: textarea });
            }
        }, 0);

    }

    goBackToCategories(): void {
        this.selectedCategory = null;
        this.selectedSubcategory = null;
        this.cdr.detectChanges();
    }

    goBackToSubcategories(): void {
        this.selectedSubcategory = null;
        this.cdr.detectChanges();
    }

    handleOutsideClick(event: MouseEvent): void {
        // Check if the click was outside the prompt library menu
        if (!this.showPromptLibraryMenu) return;

        const targetElement = event.target as HTMLElement;

        // Check if the click is inside any part of the prompt library menu
        const isInsideMenu = targetElement.closest('.prompt-library-menu') ||
            targetElement.closest('.prompt-library-dropdown') ||
            targetElement.closest('.prompt-library-menu-button');

        if (!isInsideMenu) {
            this.showPromptLibraryMenu = false;
            this.selectedCategory = null;
            this.selectedSubcategory = null;
            this.cdr.detectChanges();
        }
    }

    handleWindowResize(): void {
        // Reposition dropdown if it's open
        if (this.showPromptLibraryMenu) {
            this.positionDropdown();
        }
    }

}




